import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  FileText,
  Eye,
  Copy,
  Files,
  X,
  Mail,
  Sparkles,
  Braces,
  Paperclip,
  LayoutTemplate,
  AlignLeft,
  Save,
  ChevronDown,
} from 'lucide-react';
import { PageSkeleton } from './Skeleton';
import EmailHtmlBody from './EmailHtmlBody';
import { stripCompanyLegalFormForEmail } from '../utils/companyNameClean';
import { extractLastName } from '../utils/personName';
import SelectField from './SelectField';
import { MediaAttachmentPickerButton } from './MediaLibrarySettings';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import EmailDesignBuilder, {
  createEmptyDesign,
  parseDesignJson,
  renderDesignToHtml,
} from './EmailDesignBuilder';
import {
  looksLikeHtmlEmail,
  isDesignTemplate,
  isDesignDocumentHtml,
  designToPlainText,
  serializeDesign,
  applyPlaceholdersToHtml,
  createDefaultStyleDesign,
  textToHtmlLines,
  expandMarkdownLinksInHtml,
} from '../utils/emailDesign';
import {
  DEFAULT_VAR_OPTIONS,
  parseVarOptions,
  serializeVarOptions,
  applyNameFormat,
  buildAnredeFromOptions,
  formatContactDisplayName,
  finalizeVariableValue,
  buildUnsubscribeFooter,
  UNSUBSCRIBE_FOOTER_SOURCE,
  UNSUBSCRIBE_FOOTER_KEY,
  isUnsubscribeFooterVar,
} from '../utils/templateVariableOptions';
import { useAuth } from '../context/AuthContext';
import {
  createTemplateDraftId,
  readTemplateDrafts,
  upsertTemplateDraft,
  removeTemplateDraft,
  draftHasContent,
  snapshotTemplateEditor,
} from '../utils/templateDrafts';
import {
  REPLY_TEMPLATE_TYPES,
  REPLY_TEMPLATE_TYPE_OPTIONS,
  isReplyTemplateType,
  sortReplyTemplates,
} from '../utils/replyTemplates';
import { insertTokenIntoQuill } from './VariableInsertPanel';
import { quillHtmlToEmailHtml } from '../utils/quillEmailHtml';
import { isEmptyRichText } from '../utils/mailPreferences';
import {
  emailComposerFormats,
  emailComposerKeyboard,
  emailComposerToolbar,
  prepareEmailComposerQuill,
} from '../utils/quillToolbar';

const plainTextToQuillHtml = (text) => {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  if (!lines.length) return '<p><br></p>';
  return lines
    .map((line) => {
      const t = String(line || '');
      if (!t.trim()) return '<p><br></p>';
      // Markdown-Links [Text](https://…) → echte <a>-Links (wie beim Versand)
      return `<p>${textToHtmlLines(t)}</p>`;
    })
    .join('');
};

/** Kurzer Klartext-Ausschnitt für die Vorlagenliste (Quill-HTML → Text) */
const htmlToListSnippet = (html) => {
  const plain = String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return plain || '';
};

/** Plain-Text-Vorlagen für Quill aufbereiten; Design-Dokumente → Klartext */
const contentToQuillHtml = (content) => {
  const raw = String(content || '');
  if (!raw.trim()) return '';
  if (isDesignDocumentHtml(raw)) {
    return plainTextToQuillHtml(htmlToListSnippet(raw));
  }
  if (looksLikeHtmlEmail(raw)) return raw;
  return plainTextToQuillHtml(raw);
};

/** Textmodus-Inhalt ohne Design-Dokument speichern */
const sanitizeTextModeContentForSave = (html) => {
  const raw = String(html || '');
  if (!raw.trim()) return '';
  if (isDesignDocumentHtml(raw) || /^<!DOCTYPE\s+html/i.test(raw) || /^<html[\s>]/i.test(raw)) {
    const plain = htmlToListSnippet(raw);
    if (!plain.trim()) return '';
    return quillHtmlToEmailHtml(plainTextToQuillHtml(plain));
  }
  return raw;
};

const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const STANDARD_TEMPLATE_TYPES = new Set([]);

function isInitialTemplateType(type) {
  const t = String(type || '').trim();
  return t === 'initial' || t === 'initial_design';
}

function isFollowUp1TemplateType(type) {
  const t = String(type || '').trim();
  return t === 'follow_up_1' || t === 'follow_up_1_design';
}

function isFollowUp2TemplateType(type) {
  const t = String(type || '').trim();
  return t === 'follow_up_2' || t === 'follow_up_2_design';
}

function isFollowUp3TemplateType(type) {
  const t = String(type || '').trim();
  return t === 'follow_up_3' || t === 'follow_up_3_design';
}

/** Text vs. Design für Filter / Badges */
function isTemplateDesignFormat(template) {
  const type = String(template?.type || '').trim();
  if (/_design$/i.test(type)) return true;
  return isDesignTemplate(template);
}

/** Pro Sequenz-Typ die älteste Vorlage = feste Standard-Sequenz (nicht löschbar) */
function getPrimarySequenceIds(templates = []) {
  const primary = {};
  for (const t of templates) {
    const type = String(t?.type || '').trim();
    if (!STANDARD_TEMPLATE_TYPES.has(type)) continue;
    const id = Number(t.id);
    if (!id) continue;
    if (primary[type] == null || id < primary[type]) primary[type] = id;
  }
  return primary;
}

function isPrimarySequenceTemplate(template, primaryIds) {
  if (!template) return false;
  const type = String(template.type || '').trim();
  if (!STANDARD_TEMPLATE_TYPES.has(type)) return false;
  return Number(primaryIds?.[type]) === Number(template.id);
}

const SAMPLE_CONTACT = {
  name: 'Max Mustermann',
  name2: 'Erika Musterfrau',
  company: 'Mustermann GmbH',
  email: 'max@mustermann.de',
  phone: '+49 123 456789',
  website: 'www.mustermann.de',
  notes: 'Beispielnotiz',
  gender: 'male',
  gender2: 'female',
};

const EMPTY_VAR_FORM = () => ({
  key: '',
  value: '',
  label: '',
  source: 'custom',
  options: { ...DEFAULT_VAR_OPTIONS },
});

const CONTACT_FIELD_OPTIONS = [
  { source: 'custom', label: 'Fester Text' },
  { source: 'contact:name', label: 'Kontakt → Name' },
  { source: 'contact:company', label: 'Kontakt → Unternehmen' },
  { source: 'contact:email', label: 'Kontakt → E-Mail' },
  { source: 'contact:phone', label: 'Kontakt → Telefon' },
  { source: 'contact:website', label: 'Kontakt → Website' },
  { source: 'contact:notes', label: 'Kontakt → Notizen' },
  { source: 'contact:anrede', label: 'Kontakt → Anrede' },
];

const CONTACT_RESOLVERS = {
  'contact:name': (c, options) => formatContactDisplayName(c, options, extractLastName),
  'contact:name2': (c, options) =>
    applyNameFormat(c.name2 || '', parseVarOptions(options).nameFormat, extractLastName),
  'contact:email': (c) => c.email || '',
  'contact:company': (c) =>
    stripCompanyLegalFormForEmail(c.company || c.name || ''),
  'contact:phone': (c) => c.phone || '',
  'contact:website': (c) => c.website || '',
  'contact:notes': (c) => c.notes || '',
  'contact:anrede': (c, options) => buildAnredeFromOptions(c, options, extractLastName),
};

function isContactVar(row) {
  return String(row?.source || '').startsWith('contact:');
}

function isSystemVar(row) {
  return String(row?.source || '').startsWith('system:') || isUnsubscribeFooterVar(row);
}

function isProtectedDefaultVar(row) {
  const key = String(row?.key || '').trim().toLowerCase();
  if (!key) return false;
  if (DEFAULT_TEMPLATE_VAR_DEFS.some((d) => d.key === key)) return true;
  return false;
}

function sourceLabel(source) {
  if (source === UNSUBSCRIBE_FOOTER_SOURCE) {
    return 'Newsletter-Abmeldung (Server-Link /api/u/…)';
  }
  return CONTACT_FIELD_OPTIONS.find((o) => o.source === source)?.label || source || 'Fester Text';
}

const DEFAULT_TEMPLATE_VAR_DEFS = [
  { key: 'contact_name', label: 'Name des Kontakts', source: 'contact:name', value: '' },
  { key: 'contact_company', label: 'Unternehmen', source: 'contact:company', value: '' },
  { key: 'contact_email', label: 'E-Mail', source: 'contact:email', value: '' },
  { key: 'contact_phone', label: 'Telefon', source: 'contact:phone', value: '' },
  { key: 'contact_website', label: 'Website', source: 'contact:website', value: '' },
  { key: 'contact_anrede', label: 'Anrede (Sehr geehrte…)', source: 'contact:anrede', value: '' },
  {
    key: UNSUBSCRIBE_FOOTER_KEY,
    label: 'Abmeldehinweis',
    source: UNSUBSCRIBE_FOOTER_SOURCE,
    value: '',
  },
];

function createDefaultTemplateVars() {
  return DEFAULT_TEMPLATE_VAR_DEFS.map((d) => ({
    ...d,
    id: `local-${d.key}`,
    options: {},
    _local: true,
  }));
}

function toVariablePayload(row) {
  return {
    key: row.key,
    value: row.value != null ? String(row.value) : '',
    label: row.label || '',
    source: row.source || 'custom',
    options: serializeVarOptions(row.options),
  };
}

/** Plain-Text-Prompt zum Kopieren in eine externe KI (mit Variablen-Infos). */
function buildExternalAiPrompt({ name, typeLabel, variables }) {
  const vars = Array.isArray(variables) ? variables.filter((v) => v?.key) : [];
  const varLines = vars.length
    ? vars
        .map((v) => {
          const key = String(v.key).trim();
          const label = String(v.label || key).trim();
          const source = sourceLabel(v.source);
          const fallback =
            !isContactVar(v) && v.value != null && String(v.value).trim()
              ? ` · Standardwert: ${String(v.value).trim()}`
              : '';
          return `- {{${key}}} — ${label} (${source})${fallback}`;
        })
        .join('\n')
    : '(Noch keine Variablen angelegt — bitte ohne {{…}} schreiben oder erst Platzhalter anlegen.)';

  const lines = [
    'Schreibe bitte Betreff und Fließtext für eine deutschsprachige Marketing-/Outreach-E-Mail-Vorlage.',
    '',
    name ? `Vorlagenname: ${name}` : null,
    typeLabel ? `Vorlagentyp: ${typeLabel}` : null,
    name || typeLabel ? '' : null,
    'Anforderungen:',
    '- Persönlich, klar, ohne Floskeln; kurzer Call-to-Action.',
    '- Kein HTML, nur Fließtext (Absätze durch Leerzeilen).',
    '- Keine eckigen Platzhalter wie [Name] und keine erfundenen Beispieldaten.',
    '',
    'WICHTIG zu den Variablen:',
    '- Verwende Platzhalter EXAKT in der Schreibweise {{schluessel}} (doppelte geschweifte Klammern).',
    '- Diese Platzhalter werden später automatisch ersetzt — ersetze sie NICHT durch echte Namen/Firmen.',
    '- Erfinde keine eigenen Platzhalter.',
    '- Nutze nur Variablen aus der Liste unten, und nur wenn sie zum Inhalt passen.',
    '',
    'Verfügbare Variablen:',
    varLines,
    '',
    'Ausgabeformat:',
    'Betreff: …',
    '---',
    'Text:',
    '…',
  ];

  return lines.filter((line) => line != null).join('\n');
}

function companyLabelFromVars(variables = []) {
  const row = (variables || []).find((v) => String(v?.key || '').toLowerCase() === 'company');
  const value = row?.value != null ? String(row.value).trim() : '';
  return value || '';
}

function resolvePreviewValue(row, contact = SAMPLE_CONTACT, mailboxEmail = '', variables = [], format = 'markdown') {
  const fallback = row?.value != null ? String(row.value) : '';
  const options = parseVarOptions(row?.options);
  if (isUnsubscribeFooterVar(row)) {
    return buildUnsubscribeFooter(mailboxEmail, companyLabelFromVars(variables), '', '', { format });
  }
  let base = fallback;
  if (isContactVar(row) && CONTACT_RESOLVERS[row.source]) {
    base = CONTACT_RESOLVERS[row.source](contact, options) || fallback;
  }
  let value = finalizeVariableValue(base, options);
  const key = String(row?.key || '').trim().toLowerCase();
  if (row?.source === 'contact:company' || key === 'contact_company') {
    value = stripCompanyLegalFormForEmail(value);
  }
  return value;
}

function buildPlaceholderMap(contact = SAMPLE_CONTACT, variables = [], mailboxEmail = '', format = 'html') {
  const map = {};
  for (const row of variables) {
    if (!row?.key) continue;
    const key = String(row.key).toLowerCase();
    map[key] = {
      value: resolvePreviewValue(row, contact, mailboxEmail, variables, format),
      label: row.label || key,
      isContact: isContactVar(row),
    };
  }
  if (!map[UNSUBSCRIBE_FOOTER_KEY]) {
    map[UNSUBSCRIBE_FOOTER_KEY] = {
      value: buildUnsubscribeFooter(mailboxEmail, companyLabelFromVars(variables), '', '', { format }),
      label: 'Abmeldehinweis',
      isContact: false,
    };
  }
  return map;
}

function renderHighlightedText(text, contact = SAMPLE_CONTACT, variables = [], mailboxEmail = '') {
  const map = buildPlaceholderMap(contact, variables, mailboxEmail);
  const source = String(text || '');
  const re = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  const nodes = [];
  let lastIndex = 0;
  let match;
  let token = 0;

  while ((match = re.exec(source)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(source.slice(lastIndex, match.index));
    }
    const key = String(match[1]).toLowerCase();
    const entry = map[key];
    if (entry) {
      nodes.push(
        <mark
          key={`var-${token++}`}
          title={`{{${key}}}${entry.label ? ` · ${entry.label}` : ''}`}
          className={`rounded px-1 py-0.5 font-medium not-italic ${
            entry.isContact
              ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/50 dark:text-primary-200'
              : 'bg-accent-100 text-accent-800 dark:bg-accent-900/40 dark:text-accent-200'
          }`}
        >
          {entry.value || '—'}
        </mark>
      );
    } else {
      nodes.push(
        <mark
          key={`var-${token++}`}
          title="Unbekannte Variable"
          className="rounded px-1 py-0.5 font-mono text-[0.9em] bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300"
        >
          {match[0]}
        </mark>
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < source.length) {
    nodes.push(source.slice(lastIndex));
  }

  return nodes.length ? nodes : source;
}

function VariableOptionsFields({ form, setForm, compact = false }) {
  const [open, setOpen] = useState(false);
  const options = parseVarOptions(form.options);
  const source = form.source || 'custom';
  const labelCls = compact
    ? 'block text-[11px] font-medium text-neutral-600 dark:text-neutral-400 mb-1'
    : 'block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1';
  const inputCls = compact ? 'input-field text-sm !py-1.5' : 'input-field text-sm';
  const patchOptions = (patch) =>
    setForm((prev) => ({
      ...prev,
      options: { ...parseVarOptions(prev.options), ...patch },
    }));

  const preview = resolvePreviewValue(
    { key: form.key || 'vorschau', value: form.value, source, options },
    SAMPLE_CONTACT
  );

  return (
    <div className="rounded-xl border border-neutral-200/80 dark:border-neutral-700/80 bg-white/60 dark:bg-neutral-900/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-neutral-50/80 dark:hover:bg-neutral-800/40 transition-colors"
        aria-expanded={open}
      >
        <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">
          Erweiterte Einstellungen
        </span>
        <ChevronDown
          className={`w-4 h-4 text-neutral-400 flex-shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="space-y-3 px-3 pb-3 border-t border-neutral-200/80 dark:border-neutral-700/80 pt-3">
          {source === 'contact:name' && (
            <div>
              <label className={labelCls}>Namensformat</label>
              <SelectField
                value={options.nameFormat}
                onChange={(e) => patchOptions({ nameFormat: e.target.value })}
                className={inputCls}
              >
                <option value="full">Vollständiger Name</option>
                <option value="first">Nur Vorname</option>
                <option value="last">Nur Nachname</option>
              </SelectField>
            </div>
          )}

          {source === 'contact:anrede' && (
            <div className="grid grid-cols-1 gap-2.5">
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                Platzhalter: {'{nachname}'}, {'{vorname}'}, {'{name}'}
              </p>
              <div>
                <label className={labelCls}>Herr (mit Nachname)</label>
                <input
                  type="text"
                  value={options.anredeMale}
                  onChange={(e) => patchOptions({ anredeMale: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Herr (ohne Nachname)</label>
                <input
                  type="text"
                  value={options.anredeMaleEmpty}
                  onChange={(e) => patchOptions({ anredeMaleEmpty: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Frau (mit Nachname)</label>
                <input
                  type="text"
                  value={options.anredeFemale}
                  onChange={(e) => patchOptions({ anredeFemale: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Frau (ohne Nachname)</label>
                <input
                  type="text"
                  value={options.anredeFemaleEmpty}
                  onChange={(e) => patchOptions({ anredeFemaleEmpty: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Unbekannt / neutral</label>
                <input
                  type="text"
                  value={options.anredeUnknown}
                  onChange={(e) => patchOptions({ anredeUnknown: e.target.value })}
                  className={inputCls}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className={labelCls}>Präfix</label>
              <input
                type="text"
                value={options.prefix}
                onChange={(e) => patchOptions({ prefix: e.target.value })}
                className={inputCls}
                placeholder="z. B. „Hallo “"
              />
            </div>
            <div>
              <label className={labelCls}>Suffix</label>
              <input
                type="text"
                value={options.suffix}
                onChange={(e) => patchOptions({ suffix: e.target.value })}
                className={inputCls}
                placeholder="z. B. „,“"
              />
            </div>
            <div className={compact ? 'sm:col-span-2' : ''}>
              <label className={labelCls}>Schreibweise</label>
              <SelectField
                value={options.transform}
                onChange={(e) => patchOptions({ transform: e.target.value })}
                className={inputCls}
              >
                <option value="none">Unverändert</option>
                <option value="capitalize">Erster Buchstabe groß</option>
                <option value="upper">GROSSBUCHSTABEN</option>
                <option value="lower">kleinbuchstaben</option>
              </SelectField>
            </div>
            <div className={compact ? 'sm:col-span-2' : 'sm:col-span-2'}>
              <label className={labelCls}>Interne Notiz</label>
              <input
                type="text"
                value={options.notes}
                onChange={(e) => patchOptions({ notes: e.target.value })}
                className={inputCls}
                placeholder="Nur für dich sichtbar"
              />
            </div>
          </div>

          <div className="rounded-lg bg-neutral-100/80 dark:bg-neutral-800/60 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-0.5">
              Vorschau (Beispielkontakt)
            </p>
            <div
              className="text-sm text-neutral-800 dark:text-neutral-100 break-words [&_a]:text-primary-600 dark:[&_a]:text-primary-400 [&_a]:underline"
              dangerouslySetInnerHTML={{
                __html: textToHtmlLines(preview || '—', '#2563eb'),
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const Templates = () => {
  const { user } = useAuth();
  const mailboxEmail = String(user?.email || '').trim().toLowerCase();
  const [templates, setTemplates] = useState([]);
  const [templateVars, setTemplateVars] = useState([]);
  const [followupConfig, setFollowupConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  const [searchTerm, setSearchTerm] = useState('');
  const [formatFilter, setFormatFilter] = useState('all'); // all | text | design
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    type: 'custom',
  });
  const [editorMode, setEditorMode] = useState('text'); // text | design
  const [design, setDesign] = useState(null);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [attachmentDragOver, setAttachmentDragOver] = useState(false);
  const attachmentInputRef = useRef(null);
  const attachmentDragDepthRef = useRef(0);
  const [varSearchTerm, setVarSearchTerm] = useState('');
  const [showVarForm, setShowVarForm] = useState(false);
  const [varForm, setVarForm] = useState({ ...EMPTY_VAR_FORM() });
  const [editingVar, setEditingVar] = useState(null);
  const [savingVar, setSavingVar] = useState(false);
  const [variableToDelete, setVariableToDelete] = useState(null);
  const [deletingVariable, setDeletingVariable] = useState(false);
  const [showTextModeConfirm, setShowTextModeConfirm] = useState(false);
  const quillRef = useRef(null);
  const subjectRef = useRef(null);
  const [contentEditorKey, setContentEditorKey] = useState(0);
  const [insertTarget, setInsertTarget] = useState('content');

  const quillModules = useMemo(
    () => ({
      toolbar: emailComposerToolbar,
      clipboard: { matchVisual: false },
      keyboard: emailComposerKeyboard,
    }),
    []
  );
  const quillFormats = useMemo(() => emailComposerFormats, []);
  const [showModalVarForm, setShowModalVarForm] = useState(false);
  const [modalVarForm, setModalVarForm] = useState({ ...EMPTY_VAR_FORM() });
  const [savingModalVar, setSavingModalVar] = useState(false);
  const [templateDrafts, setTemplateDrafts] = useState(() => readTemplateDrafts());
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [editorBaseline, setEditorBaseline] = useState('');
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [varsTemplateId, setVarsTemplateId] = useState('');

  const templateTypes = [
    { value: 'custom', label: 'E-Mail-Vorlage' },
  ];

  const tabs = [
    { id: 'templates', label: 'E-Mail-Vorlagen', shortLabel: 'Vorlagen', icon: Sparkles },
    { id: 'variables', label: 'Platzhalter', shortLabel: 'Platzhalter', icon: Braces },
  ];

  useEffect(() => {
    fetchTemplates();
    fetchFollowupConfig();
  }, []);

  useEffect(() => {
    const onTemplatesChanged = () => {
      fetchTemplates({ quiet: true });
    };
    window.addEventListener('odium:templates-changed', onTemplatesChanged);
    return () => window.removeEventListener('odium:templates-changed', onTemplatesChanged);
  }, []);

  // Nach Mediathek-Änderungen: offene Vorschau / Editor-Anhänge mitziehen
  useEffect(() => {
    if (!templates.length) return;

    if (previewTemplate?.id) {
      const fresh = templates.find((t) => Number(t.id) === Number(previewTemplate.id));
      if (fresh) setPreviewTemplate(fresh);
    }

    if (editingTemplate?.id) {
      const fresh = templates.find((t) => Number(t.id) === Number(editingTemplate.id));
      if (fresh) {
        setEditingTemplate(fresh);
        // Entfernte Anhänge während des Editierens nicht wieder einspielen
        setExistingAttachments(
          (Array.isArray(fresh.attachments) ? fresh.attachments : []).filter(
            (a) => !removedAttachmentIds.includes(a.id)
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // Platzhalter sofort laden, sobald Vorlagen da sind (nicht erst beim Tab-Wechsel)
  useEffect(() => {
    if (showModal) return;
    if (!templates.length) return;

    if (!varsTemplateId) {
      setVarsTemplateId(String(templates[0].id));
      return;
    }

    const stillExists = templates.some((t) => String(t.id) === String(varsTemplateId));
    if (!stillExists) {
      setVarsTemplateId(String(templates[0].id));
      return;
    }

    fetchTemplateVars(varsTemplateId);
  }, [varsTemplateId, showModal, templates]);

  const fetchFollowupConfig = async () => {
    try {
      const response = await axios.get('/api/followup-config');
      setFollowupConfig(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Follow-up Konfiguration:', error);
    }
  };

  const fetchTemplates = async ({ quiet = false } = {}) => {
    try {
      const response = await axios.get('/api/templates');
      setTemplates(response.data);
    } catch (error) {
      if (!quiet) toast.error('Fehler beim Laden der Templates');
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateVars = async (templateId) => {
    if (!templateId) {
      setTemplateVars([]);
      return [];
    }
    try {
      const response = await axios.get('/api/template-variables', {
        params: { templateId },
      });
      const raw = response.data;
      const rows = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.variables)
          ? raw.variables
          : Array.isArray(raw?.rows)
            ? raw.rows
            : [];
      setTemplateVars(rows);
      return rows;
    } catch (error) {
      console.error('Fehler beim Laden der Variablen:', error);
      toast.error(error.response?.data?.error || 'Platzhalter konnten nicht geladen werden');
      setTemplateVars([]);
      return [];
    }
  };

  const activeVarsTemplateId = editingTemplate?.id || (!showModal ? varsTemplateId : null);

  const resetVarForm = () => {
    setVarForm({ ...EMPTY_VAR_FORM() });
    setEditingVar(null);
    setShowVarForm(false);
  };

  const handleSaveVariable = async (e) => {
    e.preventDefault();
    const key = String(varForm.key || '').trim().replace(/^\{\{|\}\}$/g, '').toLowerCase();
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      toast.error('Variablenname: klein, mit Buchstabe starten (a-z, 0-9, _)');
      return;
    }
    if (varForm.source === 'custom' && !String(varForm.value || '').trim()) {
      toast.error('Bitte einen Text für die Variable angeben');
      return;
    }

    const payload = {
      key,
      value: varForm.value || '',
      label: varForm.label || '',
      source: varForm.source || 'custom',
      options: serializeVarOptions(varForm.options),
    };
    if (isUnsubscribeFooterVar(payload) || isUnsubscribeFooterVar(key)) {
      payload.key = UNSUBSCRIBE_FOOTER_KEY;
      payload.source = UNSUBSCRIBE_FOOTER_SOURCE;
      payload.value = '';
    }

    // Neue / ungespeicherte Vorlage: nur lokal
    if (showModal && !editingTemplate?.id) {
      if (
        !editingVar &&
        templateVars.some((row) => String(row.key).toLowerCase() === key)
      ) {
        toast.error(`Variable "{{${key}}}" existiert bereits in dieser Vorlage`);
        return;
      }
      setTemplateVars((prev) => {
        if (editingVar) {
          return prev.map((row) =>
            row.id === editingVar.id
              ? { ...row, ...payload, options: parseVarOptions(payload.options), _local: true }
              : row
          );
        }
        return [
          ...prev,
          {
            id: `local-${Date.now()}-${key}`,
            ...payload,
            options: parseVarOptions(payload.options),
            _local: true,
          },
        ];
      });
      toast.success(editingVar ? 'Variable aktualisiert' : 'Variable erstellt');
      resetVarForm();
      return;
    }

    const templateId = activeVarsTemplateId;
    if (!templateId) {
      toast.error('Bitte zuerst eine Vorlage wählen');
      return;
    }

    try {
      setSavingVar(true);
      if (editingVar && !editingVar._local) {
        const { data } = await axios.put(`/api/template-variables/${editingVar.id}`, {
          ...payload,
          template_id: templateId,
        });
        toast.success(data?.message || 'Variable aktualisiert');
        if (data?.previousKey && data?.key && data.previousKey !== data.key) {
          const from = String(data.previousKey).toLowerCase();
          const to = String(data.key).toLowerCase();
          const rewrite = (text) =>
            String(text || '').replace(
              new RegExp(`\\{\\{\\s*${from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\}\\}`, 'gi'),
              `{{${to}}}`
            );
          setFormData((prev) => ({
            ...prev,
            subject: rewrite(prev.subject),
            content: rewrite(prev.content),
          }));
          setDesign((prev) => {
            if (!prev) return prev;
            try {
              return JSON.parse(rewrite(JSON.stringify(prev)));
            } catch {
              return prev;
            }
          });
        }
        if (data?.templatesUpdated > 0) {
          fetchTemplates();
        }
      } else {
        await axios.post('/api/template-variables', {
          ...payload,
          template_id: templateId,
        });
        toast.success('Variable erstellt');
      }
      resetVarForm();
      await fetchTemplateVars(templateId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Speichern der Variable');
    } finally {
      setSavingVar(false);
    }
  };

  const handleSaveModalVariable = async () => {
    const key = String(modalVarForm.key || '').trim().replace(/^\{\{|\}\}$/g, '').toLowerCase();
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      toast.error('Variablenname: klein, mit Buchstabe starten (a-z, 0-9, _)');
      return;
    }
    const usesContact = String(modalVarForm.source || '').startsWith('contact:');
    if (!usesContact && !String(modalVarForm.value || '').trim()) {
      toast.error('Bitte einen Text für die Variable angeben');
      return;
    }

    const payload = {
      key,
      value: modalVarForm.value || '',
      label: modalVarForm.label || '',
      source: modalVarForm.source || 'custom',
      options: serializeVarOptions(modalVarForm.options),
    };

    if (!editingTemplate?.id) {
      if (templateVars.some((row) => String(row.key).toLowerCase() === key)) {
        toast.error(`Variable "{{${key}}}" existiert bereits in dieser Vorlage`);
        return;
      }
      setTemplateVars((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}-${key}`,
          ...payload,
          options: parseVarOptions(payload.options),
          _local: true,
        },
      ]);
      toast.success('Variable erstellt');
      setShowModalVarForm(false);
      setModalVarForm({ ...EMPTY_VAR_FORM() });
      insertPlaceholder(key);
      return;
    }

    try {
      setSavingModalVar(true);
      await axios.post('/api/template-variables', {
        ...payload,
        template_id: editingTemplate.id,
      });
      toast.success('Variable erstellt');
      setShowModalVarForm(false);
      setModalVarForm({ ...EMPTY_VAR_FORM() });
      await fetchTemplateVars(editingTemplate.id);
      insertPlaceholder(key);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Speichern der Variable');
    } finally {
      setSavingModalVar(false);
    }
  };

  const handleEditVariable = (row) => {
    setEditingVar(row);
    setVarForm({
      key: row.key,
      value: row.value || '',
      label: row.label || '',
      source: row.source || 'custom',
      options: parseVarOptions(row.options),
    });
    setShowVarForm(true);
    setActiveTab('variables');
  };

  const handleDeleteVariable = (row) => {
    if (isProtectedDefaultVar(row)) {
      toast.error(`Standard-Variable "{{${row.key}}}" kann nicht gelöscht werden`);
      return;
    }
    setVariableToDelete(row);
  };

  const confirmDeleteVariable = async () => {
    if (!variableToDelete) return;
    setDeletingVariable(true);
    try {
      if (variableToDelete._local || String(variableToDelete.id).startsWith('local-')) {
        setTemplateVars((prev) => prev.filter((row) => row.id !== variableToDelete.id));
        toast.success('Variable gelöscht');
        if (editingVar?.id === variableToDelete.id) resetVarForm();
        setVariableToDelete(null);
        return;
      }
      await axios.delete(`/api/template-variables/${variableToDelete.id}`);
      toast.success('Variable gelöscht');
      if (editingVar?.id === variableToDelete.id) resetVarForm();
      setVariableToDelete(null);
      const tid = activeVarsTemplateId || variableToDelete.template_id;
      if (tid) await fetchTemplateVars(tid);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setDeletingVariable(false);
    }
  };

  const insertPlaceholder = (key, field = insertTarget) => {
    const token = `{{${key}}}`;
    if (field === 'subject') {
      const el = subjectRef.current;
      const current = formData.subject || '';
      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = current.slice(0, start) + token + current.slice(end);
        setFormData((prev) => ({ ...prev, subject: next }));
        requestAnimationFrame(() => {
          el.focus();
          const pos = start + token.length;
          el.setSelectionRange(pos, pos);
        });
      } else {
        setFormData((prev) => ({ ...prev, subject: current + token }));
      }
      return;
    }

    if (editorMode === 'design') {
      setDesign((prev) => {
        const active = prev || createEmptyDesign();
        const textKeys = ['content', 'title', 'subtitle', 'headline', 'subtext', 'label', 'leftText', 'rightText'];
        const targetBlock =
          active.blocks.find((x) => x.type === 'text') || active.blocks[0];
        if (!targetBlock) return createEmptyDesign(token);
        const blocks = active.blocks.map((b) => {
          if (b.id !== targetBlock.id) return b;
          const props = { ...b.props };
          const field = textKeys.find((k) => k in props) || 'content';
          props[field] = `${props[field] || ''}${token}`;
          return { ...b, props };
        });
        return { ...active, blocks };
      });
      toast.success(`{{${key}}} in Design eingefügt`);
      return;
    }

    if (!insertTokenIntoQuill(quillRef, token)) {
      setFormData((prev) => ({ ...prev, content: `${prev.content || ''}${token}` }));
    }
  };

  const handleContentQuillChange = (val, _delta, source) => {
    if (source !== 'user') return;
    setFormData((prev) => ({
      ...prev,
      content: isEmptyRichText(val) ? '' : val,
    }));
  };

  const getTextModeContentForSave = () => {
    try {
      const editor = quillRef.current?.getEditor?.();
      if (editor) {
        const raw = editor.root?.innerHTML || '';
        if (isEmptyRichText(raw)) return '';
        return sanitizeTextModeContentForSave(quillHtmlToEmailHtml(raw));
      }
    } catch (_) {
      /* ignore */
    }
    const raw = formData.content || '';
    if (isEmptyRichText(raw)) return '';
    const converted = looksLikeHtmlEmail(raw) ? quillHtmlToEmailHtml(raw) : raw;
    return sanitizeTextModeContentForSave(converted);
  };

  const uploadFilesToTemplate = async (templateId, files) => {
    if (!templateId || !files.length) return;
    const uploadData = new FormData();
    const sourcePublicIds = files.map((file) => file.mediaPublicId || '');
    files.forEach((file) => uploadData.append('attachments', file));
    uploadData.append('sourcePublicIds', JSON.stringify(sourcePublicIds));
    await axios.post(`/api/templates/${templateId}/attachments`, uploadData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const openTemplateAttachmentPreview = (att) => {
    if (!att?.id) return;
    const url = `/api/template-attachments/${att.id}`;
    setPreviewAttachment({
      url: `${url}?inline=1`,
      downloadUrl: url,
      filename: att.filename || 'Anhang',
      contentType: att.contentType || att.content_type || 'application/octet-stream',
    });
  };

  const filesFromExistingAttachments = async (atts) => {
    const files = [];
    for (const att of atts) {
      const res = await axios.get(`/api/template-attachments/${att.id}`, { responseType: 'blob' });
      files.push(new File([res.data], att.filename || 'anhang', {
        type: att.contentType || res.data.type || 'application/octet-stream',
      }));
    }
    return files;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let content = formData.content;
    let design_json = null;

    if (editorMode === 'design') {
      const activeDesign = design || createEmptyDesign();
      content = renderDesignToHtml(activeDesign);
      design_json = serializeDesign(activeDesign);
    } else {
      content = getTextModeContentForSave();
      design_json = null;
      // Design-State und -Dokumente vollständig verwerfen
      if (design) setDesign(null);
    }

    if (!formData.name || isEmptyRichText(content)) {
      toast.error('Name und Inhalt sind Pflichtfelder');
      return;
    }
    if (!String(formData.subject || '').trim()) {
      toast.error('Alle Felder sind Pflichtfelder');
      return;
    }

    const payload = {
      ...formData,
      type: 'custom',
      content,
      design_json: editorMode === 'design' ? design_json : null,
    };
    if (!editingTemplate) {
      payload.variables = templateVars.map(toVariablePayload);
    }

    try {
      setSavingTemplate(true);
      let templateId = editingTemplate?.id || null;

      if (editingTemplate) {
        await axios.put(`/api/templates/${editingTemplate.id}`, payload);
        for (const attId of removedAttachmentIds) {
          await axios.delete(`/api/templates/${editingTemplate.id}/attachments/${attId}`);
        }
        await uploadFilesToTemplate(editingTemplate.id, pendingAttachments);
      } else {
        const res = await axios.post('/api/templates', payload);
        templateId = res.data?.id;
        const copied = existingAttachments.length
          ? await filesFromExistingAttachments(existingAttachments)
          : [];
        await uploadFilesToTemplate(templateId, [...copied, ...pendingAttachments]);
      }

      toast.success(editingTemplate ? 'Template erfolgreich aktualisiert' : 'Template erfolgreich erstellt');
      if (activeDraftId) {
        setTemplateDrafts(removeTemplateDraft(activeDraftId));
      }
      fetchTemplates();
      resetForm({ skipConfirm: true });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Speichern des Templates');
      console.error('Error saving template:', error);
    } finally {
      setSavingTemplate(false);
    }
  };

  const getCurrentSnapshot = () =>
    snapshotTemplateEditor({
      formData,
      editorMode,
      design,
      existingAttachments,
      pendingAttachments,
      removedAttachmentIds,
    });

  const isEditorDirty = () => {
    if (!showModal) return false;
    if (!editorBaseline) return draftHasContent({ formData, design });
    return getCurrentSnapshot() !== editorBaseline;
  };

  const persistActiveDraft = () => {
    if (!activeDraftId || editingTemplate) return null;
    const safeMode = design && editorMode === 'design' ? 'design' : 'text';
    const draft = {
      id: activeDraftId,
      formData,
      editorMode: safeMode,
      design: safeMode === 'design' ? design : null,
      templateVars,
    };
    if (!draftHasContent(draft)) return null;
    const list = upsertTemplateDraft(draft);
    setTemplateDrafts(list);
    return draft;
  };

  const loadTemplateIntoForm = (template, { asCopy = false, draftId = null } = {}) => {
    const parsed = parseDesignJson(template.design_json);
    const isDesign = isDesignTemplate(template);
    const nextForm = {
      name: asCopy ? `${template.name} (Kopie)` : template.name,
      subject: template.subject,
      content: isDesign ? template.content : contentToQuillHtml(template.content),
      type: 'custom',
    };
    const nextMode = isDesign ? 'design' : 'text';
    const nextDesign = isDesign ? parsed : null;
    const nextExisting = template.attachments || [];
    setFormData(nextForm);
    setEditorMode(nextMode);
    setDesign(nextDesign);
    setExistingAttachments(nextExisting);
    setPendingAttachments([]);
    setRemovedAttachmentIds([]);
    setActiveDraftId(draftId);
    setContentEditorKey((k) => k + 1);
    setShowModal(true);
    setLeaveConfirmOpen(false);
    // Baseline nach State-Update im nächsten Tick setzen
    setTimeout(() => {
      setEditorBaseline(
        snapshotTemplateEditor({
          formData: nextForm,
          editorMode: nextMode,
          design: nextDesign,
          existingAttachments: nextExisting,
          pendingAttachments: [],
          removedAttachmentIds: [],
        })
      );
    }, 0);
  };

  const handleEdit = async (template) => {
    setEditingTemplate(template);
    loadTemplateIntoForm(template, { draftId: null });
    await fetchTemplateVars(template.id);
  };

  const handlePreview = async (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
    await fetchTemplateVars(template.id);
  };

  const getDesignHtmlForClipboard = (template) => {
    const stored = String(template?.content || '').trim();
    if (isDesignDocumentHtml(stored) || looksLikeHtmlEmail(stored)) {
      return stored;
    }
    const parsed = parseDesignJson(template?.design_json);
    if (parsed) return renderDesignToHtml(parsed);
    return stored;
  };

  const getTemplateContentForClipboard = (template) => {
    if (isTemplateDesignFormat(template)) {
      return getDesignHtmlForClipboard(template);
    }
    const raw = String(template?.content || '').trim();
    if (!raw) return '';
    if (/<[a-z][\s\S]*>/i.test(raw)) return htmlToListSnippet(raw);
    return raw;
  };

  /** Inhalt in die Zwischenablage (Design-HTML bzw. Text-Inhalt) */
  const handleCopy = async (template) => {
    const isDesign = isTemplateDesignFormat(template);
    const text = getTemplateContentForClipboard(template);
    if (!text.trim()) {
      toast.error(
        isDesign ? 'Kein Design-HTML zum Kopieren vorhanden' : 'Kein Inhalt zum Kopieren vorhanden'
      );
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(
        isDesign ? 'Design-HTML in die Zwischenablage kopiert' : 'Inhalt in die Zwischenablage kopiert'
      );
    } catch (_) {
      toast.error('Kopieren in die Zwischenablage fehlgeschlagen');
    }
  };

  /** Vorlage als neue eigene Kopie im Editor öffnen */
  const handleDuplicate = async (template) => {
    setEditingTemplate(null);
    loadTemplateIntoForm(template, { asCopy: true, draftId: createTemplateDraftId() });
    try {
      const rows = await axios.get('/api/template-variables', {
        params: { templateId: template.id },
      });
      setTemplateVars(
        (rows.data || []).map((v) => ({
          ...v,
          id: `local-copy-${v.key}`,
          options: parseVarOptions(v.options),
          _local: true,
        }))
      );
    } catch (_) {
      setTemplateVars(createDefaultTemplateVars());
    }
  };

  const handleDelete = (template) => {
    setTemplateToDelete(template);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete || deletingTemplate) return;
    setDeletingTemplate(true);
    try {
      await axios.delete(`/api/templates/${templateToDelete.id}`);
      toast.success('Template erfolgreich gelöscht');
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen des Templates');
      console.error('Error deleting template:', error);
    } finally {
      setDeletingTemplate(false);
    }
  };

  const addPendingAttachments = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setPendingAttachments((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= 10) break;
        if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
        next.push(file);
      }
      if (next.length >= 10) toast.error('Maximal 10 Anhänge pro Vorlage');
      return next;
    });
  };

  const removeExistingAttachment = (id) => {
    setExistingAttachments((prev) => prev.filter((a) => a.id !== id));
    if (editingTemplate) {
      setRemovedAttachmentIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  };

  const removePendingAttachment = (index) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = ({ skipConfirm = false } = {}) => {
    if (!skipConfirm && showModal && isEditorDirty()) {
      // Leerer neuer Entwurf: still schließen und Cache aufräumen
      if (
        !editingTemplate &&
        !draftHasContent({ formData, design }) &&
        pendingAttachments.length === 0
      ) {
        if (activeDraftId) setTemplateDrafts(removeTemplateDraft(activeDraftId));
        // fall through to close
      } else {
        setLeaveConfirmOpen(true);
        return;
      }
    } else if (
      !skipConfirm &&
      showModal &&
      !editingTemplate &&
      activeDraftId &&
      !draftHasContent({ formData, design })
    ) {
      setTemplateDrafts(removeTemplateDraft(activeDraftId));
    }
    setFormData({
      name: '',
      subject: '',
      content: '',
      type: 'custom',
    });
    setEditorMode('text');
    setDesign(null);
    setExistingAttachments([]);
    setPendingAttachments([]);
    setRemovedAttachmentIds([]);
    setAttachmentDragOver(false);
    attachmentDragDepthRef.current = 0;
    setTemplateVars([]);
    setShowModalVarForm(false);
    setModalVarForm({ ...EMPTY_VAR_FORM() });
    setShowTextModeConfirm(false);
    setEditingTemplate(null);
    setActiveDraftId(null);
    setEditorBaseline('');
    setLeaveConfirmOpen(false);
    setShowModal(false);
  };

  const saveDraftAndClose = () => {
    if (!editingTemplate) {
      const saved = persistActiveDraft();
      if (saved) {
        toast.success('Entwurf gespeichert');
      } else if (activeDraftId) {
        setTemplateDrafts(removeTemplateDraft(activeDraftId));
      }
    }
    resetForm({ skipConfirm: true });
  };

  const discardAndClose = () => {
    if (!editingTemplate && activeDraftId) {
      setTemplateDrafts(removeTemplateDraft(activeDraftId));
      toast.success('Entwurf verworfen');
    }
    resetForm({ skipConfirm: true });
  };

  const openDraft = (draft) => {
    if (!draft?.id) return;
    setEditingTemplate(null);
    const rawContent = draft.formData?.content || '';
    const nextMode = draft.editorMode === 'design' ? 'design' : 'text';
    const nextForm = {
      name: draft.formData?.name || '',
      subject: draft.formData?.subject || '',
      content: nextMode === 'text' ? contentToQuillHtml(rawContent) : rawContent,
      type: draft.formData?.type || 'custom',
    };
    const nextDesign =
      nextMode === 'design' && draft.design && typeof draft.design === 'object'
        ? draft.design
        : null;
    setFormData(nextForm);
    setEditorMode(nextMode);
    setDesign(nextDesign);
    setExistingAttachments([]);
    setPendingAttachments([]);
    setRemovedAttachmentIds([]);
    setTemplateVars(
      Array.isArray(draft.templateVars) && draft.templateVars.length
        ? draft.templateVars
        : createDefaultTemplateVars()
    );
    setActiveDraftId(draft.id);
    setContentEditorKey((k) => k + 1);
    setShowModal(true);
    setLeaveConfirmOpen(false);
    setTimeout(() => {
      setEditorBaseline(
        snapshotTemplateEditor({
          formData: nextForm,
          editorMode: nextMode,
          design: nextDesign,
          existingAttachments: [],
          pendingAttachments: [],
          removedAttachmentIds: [],
        })
      );
    }, 0);
  };

  const requestDeleteDraft = (draft) => {
    if (!draft?.id) return;
    setDraftToDelete(draft);
  };

  const confirmDeleteDraft = () => {
    const draftId = draftToDelete?.id;
    if (!draftId) return;
    setTemplateDrafts(removeTemplateDraft(draftId));
    if (activeDraftId === draftId) {
      resetForm({ skipConfirm: true });
    }
    setDraftToDelete(null);
    toast.success('Entwurf gelöscht');
  };

  const groupTemplatesByType = (list, allTemplates = list) => {
    const primaryIds = getPrimarySequenceIds(allTemplates);
    const groups = {
      initial: [],
      follow_up_1: [],
      follow_up_2: [],
      follow_up_3: [],
      replies: [],
      custom: [],
    };
    list.forEach((template) => {
      // Nur die feste Sequenz-Vorlage pro Typ im Sequenz-Tab;
      // weitere (auch Typ Erstkontakt) landen unter „Eigene Vorlagen“
      if (isPrimarySequenceTemplate(template, primaryIds)) {
        if (isReplyTemplateType(template.type)) {
          groups.replies.push(template);
        } else if (isInitialTemplateType(template.type)) {
          groups.initial.push(template);
        } else if (isFollowUp1TemplateType(template.type)) {
          groups.follow_up_1.push(template);
        } else if (isFollowUp2TemplateType(template.type)) {
          groups.follow_up_2.push(template);
        } else if (isFollowUp3TemplateType(template.type)) {
          groups.follow_up_3.push(template);
        } else if (groups[template.type]) {
          groups[template.type].push(template);
        } else {
          groups.custom.push(template);
        }
      } else {
        groups.custom.push(template);
      }
    });
    groups.initial.sort((a, b) => {
      const order = { initial: 0, initial_design: 1 };
      const oa = order[a.type] ?? 9;
      const ob = order[b.type] ?? 9;
      if (oa !== ob) return oa - ob;
      return String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' });
    });
    groups.follow_up_1.sort((a, b) => {
      const order = { follow_up_1: 0, follow_up_1_design: 1 };
      const oa = order[a.type] ?? 9;
      const ob = order[b.type] ?? 9;
      if (oa !== ob) return oa - ob;
      return String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' });
    });
    groups.follow_up_2.sort((a, b) => {
      const order = { follow_up_2: 0, follow_up_2_design: 1 };
      const oa = order[a.type] ?? 9;
      const ob = order[b.type] ?? 9;
      if (oa !== ob) return oa - ob;
      return String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' });
    });
    groups.follow_up_3.sort((a, b) => {
      const order = { follow_up_3: 0, follow_up_3_design: 1 };
      const oa = order[a.type] ?? 9;
      const ob = order[b.type] ?? 9;
      if (oa !== ob) return oa - ob;
      return String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' });
    });
    groups.replies = sortReplyTemplates(groups.replies);
    return groups;
  };

  const getActiveFollowUpCount = () => {
    return followupConfig.filter((c) => c.enabled === 1).length;
  };

  const extractPreviewBodyHtml = (html) => {
    const raw = String(html || '').trim();
    if (!raw) return '';
    const bodyMatch = raw.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) return bodyMatch[1].trim();
    return raw
      .replace(/<!DOCTYPE[^>]*>/i, '')
      .replace(/<\/?html[^>]*>/gi, '')
      .replace(/<head[\s\S]*?<\/head>/gi, '')
      .replace(/<\/?body[^>]*>/gi, '')
      .trim();
  };

  const renderPreviewBody = (template) => {
    const content = template?.content || '';
    const parsed = parseDesignJson(template?.design_json);
    const asDesign = isDesignTemplate(template);
    if (asDesign || looksLikeHtmlEmail(content)) {
      const map = buildPlaceholderMap(SAMPLE_CONTACT, templateVars, mailboxEmail);
      // Aliase nur für gleichbedeutende Kontakt-Keys (nicht company ↔ contact_company)
      if (!map.contact_name && map.name) map.contact_name = { ...map.name };
      if (!map.contact_email && map.email) map.contact_email = { ...map.email };
      if (!map.name && map.contact_name) map.name = { ...map.contact_name };
      if (!map.email && map.contact_email) map.email = { ...map.contact_email };
      if (!map.contact_name) {
        map.contact_name = { value: SAMPLE_CONTACT.name, label: 'Name', isContact: true };
      }
      if (!map.contact_company) {
        map.contact_company = {
          value: stripCompanyLegalFormForEmail(SAMPLE_CONTACT.company),
          label: 'Unternehmen',
          isContact: true,
        };
      }
      if (!map.contact_email) {
        map.contact_email = { value: SAMPLE_CONTACT.email, label: 'E-Mail', isContact: true };
      }

      // Design-Mails behalten ihre eigenen Farben (E-Mail-Layout)
      if (asDesign && parsed) {
        const html = expandMarkdownLinksInHtml(
          applyPlaceholdersToHtml(renderDesignToHtml(parsed), map, { highlight: true })
        );
        const pageBg = String(parsed?.settings?.backgroundColor || '#09090b').trim();
        return (
          <iframe
            title="Design-Vorschau"
            srcDoc={html}
            className="w-full h-[min(68vh,38rem)] rounded-xl border border-neutral-700"
            style={{ backgroundColor: pageBg }}
            sandbox=""
          />
        );
      }

      // Text: gleiche Aufbereitung wie beim Versand + Variablen farblich markiert
      const emailHtml = quillHtmlToEmailHtml(extractPreviewBodyHtml(content));
      const html = expandMarkdownLinksInHtml(
        applyPlaceholdersToHtml(emailHtml, map, { highlight: true })
      );
      return (
        <div className="rounded-xl border border-neutral-200/80 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-950/50 px-4 py-4 sm:px-5 sm:py-5">
          <EmailHtmlBody
            html={html}
            className="template-text-preview email-html-frame max-w-none text-[15px] text-neutral-800 dark:text-neutral-200"
            allowExternalImages
          />
        </div>
      );
    }
    return (
      <div className="rounded-xl border border-neutral-200/80 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-950/50 px-4 py-4 sm:px-5 sm:py-5">
        <EmailHtmlBody
          html={applyPlaceholdersToHtml(
            textToHtmlLines(String(content || '')),
            buildPlaceholderMap(SAMPLE_CONTACT, templateVars, mailboxEmail),
            { highlight: true }
          )}
          className="template-text-preview email-html-frame max-w-none text-[15px] text-neutral-800 dark:text-neutral-200"
          allowExternalImages
        />
      </div>
    );
  };

  const isDesignPreview =
    !!previewTemplate && isDesignTemplate(previewTemplate);

  const discardDesignAndSwitchToText = () => {
    const plainFromDesign = designToPlainText(design);
    const nextContent = plainFromDesign
      ? plainTextToQuillHtml(plainFromDesign)
      : contentToQuillHtml(formData.content);
    setShowTextModeConfirm(false);
    setDesign(null);
    setFormData((prev) => ({ ...prev, content: nextContent }));
    setContentEditorKey((k) => k + 1);
    setEditorMode('text');
  };

  const switchEditorMode = (mode) => {
    if (mode === editorMode) return;
    if (mode === 'design') {
      const raw = formData.content || '';
      const seed =
        raw && !looksLikeHtmlEmail(raw)
          ? raw
          : raw
            ? String(raw)
                .replace(/<[^>]+>/g, ' ')
                .replace(/&nbsp;/gi, ' ')
                .replace(/\s+/g, ' ')
                .trim()
            : '';
      setDesign(createDefaultStyleDesign(seed));
      setEditorMode('design');
      return;
    }
    // Textmodus: Design immer verwerfen (nicht im Speicher behalten)
    if (design || isDesignDocumentHtml(formData.content)) {
      setShowTextModeConfirm(true);
      return;
    }
    setContentEditorKey((k) => k + 1);
    setEditorMode('text');
  };

  const confirmSwitchToTextMode = () => {
    discardDesignAndSwitchToText();
  };

  const openNewTemplate = (preferredType) => {
    const draftId = createTemplateDraftId();
    const nextForm = {
      name: '',
      subject: '',
      content: '',
      type: 'custom',
    };
    setEditingTemplate(null);
    setFormData(nextForm);
    setEditorMode('text');
    setDesign(null);
    setExistingAttachments([]);
    setPendingAttachments([]);
    setRemovedAttachmentIds([]);
    setTemplateVars(createDefaultTemplateVars());
    setActiveDraftId(draftId);
    setContentEditorKey((k) => k + 1);
    setShowModal(true);
    setLeaveConfirmOpen(false);
    setTimeout(() => {
      setEditorBaseline(
        snapshotTemplateEditor({
          formData: nextForm,
          editorMode: 'text',
          design: null,
          existingAttachments: [],
          pendingAttachments: [],
          removedAttachmentIds: [],
        })
      );
    }, 0);
  };

  // Neue Vorlage / Kopie: laufend im localStorage cachen
  useEffect(() => {
    if (!showModal || editingTemplate || !activeDraftId) return undefined;
    const timer = setTimeout(() => {
      const safeMode = design && editorMode === 'design' ? 'design' : 'text';
      const draft = {
        id: activeDraftId,
        formData,
        editorMode: safeMode,
        design: safeMode === 'design' ? design : null,
        templateVars,
      };
      if (!draftHasContent(draft)) return;
      setTemplateDrafts(upsertTemplateDraft(draft));
    }, 400);
    return () => clearTimeout(timer);
  }, [showModal, editingTemplate, activeDraftId, formData, editorMode, design, templateVars]);

  useEffect(() => {
    if (!showModal || editorMode !== 'text') return undefined;
    const timer = window.setTimeout(() => {
      try {
        prepareEmailComposerQuill(quillRef.current?.getEditor?.());
      } catch (_) {
        /* ignore */
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [showModal, editorMode, contentEditorKey]);

  useEffect(() => {
    if (!showModal) return undefined;
    const onBeforeUnload = (e) => {
      if (!isEditorDirty()) return;
      if (!editingTemplate && activeDraftId) persistActiveDraft();
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur bei offenen Änderungen warnen
  }, [showModal, editorBaseline, formData, editorMode, design, editingTemplate, activeDraftId, existingAttachments, pendingAttachments, removedAttachmentIds]);

  const designPlaceholderMap = useMemo(() => {
    const map = {
      name: SAMPLE_CONTACT.name,
      company: SAMPLE_CONTACT.company,
      email: SAMPLE_CONTACT.email,
      phone: SAMPLE_CONTACT.phone,
      website: SAMPLE_CONTACT.website,
    };
    for (const row of templateVars) {
      if (!row?.key) continue;
      map[String(row.key).toLowerCase()] = resolvePreviewValue(
        row,
        SAMPLE_CONTACT,
        mailboxEmail,
        templateVars,
        'html'
      );
    }
    if (!map[UNSUBSCRIBE_FOOTER_KEY]) {
      map[UNSUBSCRIBE_FOOTER_KEY] = buildUnsubscribeFooter(
        mailboxEmail,
        companyLabelFromVars(templateVars),
        '',
        '',
        { format: 'html' }
      );
    }
    return map;
  }, [templateVars, mailboxEmail]);

  const typeLabelForAi =
    templateTypes.find((t) => t.value === formData.type)?.label || formData.type || '';

  const externalAiPrompt = useMemo(
    () =>
      buildExternalAiPrompt({
        name: String(formData.name || '').trim(),
        typeLabel: typeLabelForAi,
        variables: templateVars,
      }),
    [formData.name, typeLabelForAi, templateVars]
  );

  const handleCopyExternalAiPrompt = async () => {
    try {
      await navigator.clipboard.writeText(externalAiPrompt);
      toast.success('Prompt kopiert');
    } catch (_) {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const contactCount = templateVars.filter(isContactVar).length;
  const customCount = templateVars.length - contactCount;
  const usesContactSource = String(varForm.source || '').startsWith('contact:');
  const usesSystemSource = isSystemVar(varForm) || isUnsubscribeFooterVar(varForm);

  const filteredVars = templateVars.filter((row) => {
    const q = varSearchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      String(row.key || '').toLowerCase().includes(q) ||
      String(row.label || '').toLowerCase().includes(q) ||
      String(row.value || '').toLowerCase().includes(q) ||
      String(sourceLabel(row.source) || '').toLowerCase().includes(q)
    );
  });

  const filteredTemplates = templates.filter((template) => {
    const q = String(searchTerm || '').trim().toLowerCase();
    const matchesSearch =
      !q ||
      String(template.name || '')
        .toLowerCase()
        .includes(q) ||
      String(template.subject || '')
        .toLowerCase()
        .includes(q);
    if (!matchesSearch) return false;
    if (formatFilter === 'all') return true;
    const isDesign = isTemplateDesignFormat(template);
    return formatFilter === 'design' ? isDesign : !isDesign;
  });

  const formatFilterOptions = [
    { id: 'all', label: 'Alle', icon: Mail },
    { id: 'text', label: 'Text', icon: AlignLeft },
    { id: 'design', label: 'Design', icon: LayoutTemplate },
  ];

  const hasListFilter = Boolean(String(searchTerm || '').trim()) || formatFilter !== 'all';
  const listFilterHint =
    formatFilter === 'text'
      ? 'Keine Text-Vorlagen in dieser Ansicht. Filter auf „Alle“ oder „Design“ stellen.'
      : formatFilter === 'design'
        ? 'Keine Design-Vorlagen in dieser Ansicht. Filter auf „Alle“ oder „Text“ stellen.'
        : 'Anderen Suchbegriff versuchen.';

  const grouped = groupTemplatesByType(filteredTemplates, templates);
  const followUpItems = [...grouped.follow_up_1, ...grouped.follow_up_2, ...grouped.follow_up_3];
  const firstReplyItems = grouped.replies || [];
  const primarySequenceIds = getPrimarySequenceIds(templates);

  const TemplateActions = ({ template }) => {
    const isDesign = isTemplateDesignFormat(template);
    return (
    <div className="flex items-center gap-0.5 flex-shrink-0">
      <button
        type="button"
        onClick={() => handlePreview(template)}
        className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
        title="Vorschau"
      >
        <Eye className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleCopy(template);
        }}
        className="p-2 rounded-lg text-neutral-400 hover:text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20"
        title={isDesign ? 'HTML in Zwischenablage kopieren' : 'Inhalt in Zwischenablage kopieren'}
      >
        <Copy className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDuplicate(template);
        }}
        className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
        title="Duplizieren"
      >
        <Files className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => handleEdit(template)}
        className="p-2 rounded-lg text-neutral-400 hover:text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/20"
        title="Bearbeiten"
      >
        <Edit className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => handleDelete(template)}
        className="p-2 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
        title="Löschen"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
    );
  };

  const renderTemplateRow = (template, opts = {}) => {
    return (
      <div
        key={template.id}
        className="group flex items-start gap-3 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 hover:bg-neutral-50/80 dark:hover:bg-neutral-950/40 transition-colors"
      >
        {opts.step != null && (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 flex items-center justify-center text-xs font-semibold mt-0.5">
            {opts.step}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                {template.name}
              </h3>
              <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-300 truncate">
                {String(template.subject || '').trim() ? template.subject : '(Kein Betreff)'}
              </p>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 line-clamp-2 leading-relaxed whitespace-pre-line">
                {isDesignTemplate(template)
                  ? 'Gestaltungte HTML-Vorlage'
                  : htmlToListSnippet(template.content) || template.content}
              </p>
              {(isDesignTemplate(template) || (template.attachments || []).length > 0) && (
                <div className="mt-2 flex flex-col items-start gap-[0.55rem]">
                  {isDesignTemplate(template) && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/30 px-2 py-0.5 rounded-md">
                      <LayoutTemplate className="w-3 h-3" />
                      Design
                    </span>
                  )}
                  {(template.attachments || []).length > 0 && (
                    <p className="inline-flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                      <Paperclip className="w-3.5 h-3.5" />
                      {template.attachments.length} Anhang{template.attachments.length === 1 ? '' : 'e'}
                      <span className="text-neutral-400 dark:text-neutral-500 truncate max-w-[14rem]">
                        · {template.attachments.map((a) => a.filename).join(', ')}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>
            <TemplateActions template={template} />
          </div>
        </div>
      </div>
    );
  };

  const EmptyState = ({ title, description, actionLabel, onAction }) => (
    <div className="px-5 py-14 text-center">
      <FileText className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
      <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{title}</h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 max-w-sm mx-auto">{description}</p>
      {actionLabel && onAction && (
        <button type="button" onClick={onAction} className="btn-primary mt-5 inline-flex items-center">
          <Plus className="w-4 h-4 mr-2" />
          {actionLabel}
        </button>
      )}
    </div>
  );

  if (loading) {
    return <PageSkeleton variant="templates" />;
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 fade-in">
        <div className="min-w-0">
          <h1 className="page-title">Vorlagen</h1>
          <p className="page-subtitle hidden sm:block">
            Eigene E-Mail-Vorlagen und Platzhalter
          </p>
        </div>
        {activeTab !== 'variables' && (
          <button
            type="button"
            onClick={() => openNewTemplate()}
            className="btn-primary !py-2 !px-3 sm:!py-2.5 sm:!px-4 flex items-center flex-shrink-0 touch-manipulation"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="sm:hidden">Neu</span>
            <span className="hidden sm:inline">Neue Vorlage</span>
          </button>
        )}
        {activeTab === 'variables' && !showVarForm && (
          <button
            type="button"
            disabled={!varsTemplateId}
            onClick={() => {
              if (!varsTemplateId) {
                toast.error('Bitte zuerst eine Vorlage wählen');
                return;
              }
              setEditingVar(null);
              setVarForm({ ...EMPTY_VAR_FORM() });
              setShowVarForm(true);
            }}
            className="btn-primary !py-2 !px-3 sm:!py-2.5 sm:!px-4 flex items-center flex-shrink-0 disabled:opacity-50 touch-manipulation"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="sm:hidden">Neu</span>
            <span className="hidden sm:inline">Variable hinzufügen</span>
          </button>
        )}
      </div>

      {/* Tabs — Mobile: Segment-Control, Desktop: Unterstreichung */}
      <div className="fade-in">
        <nav
          className="grid grid-cols-2 gap-0.5 p-1 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-100/80 dark:bg-neutral-900/80 sm:flex sm:gap-1 sm:-mb-px sm:overflow-x-auto sm:rounded-none sm:border-0 sm:border-b sm:border-neutral-200 dark:sm:border-neutral-800 sm:bg-transparent sm:p-0"
          aria-label="Vorlagen Bereiche"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const count =
              tab.id === 'templates'
                ? filteredTemplates.length
                : templateVars.length;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 px-1.5 py-2 sm:px-4 sm:py-3 text-[11px] sm:text-sm font-medium rounded-lg sm:rounded-none border-b-0 sm:border-b-2 whitespace-nowrap transition-colors touch-manipulation min-h-[3rem] sm:min-h-0 ${
                  isActive
                    ? 'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50 shadow-sm sm:shadow-none sm:bg-transparent sm:border-primary-600 sm:text-primary-700 dark:sm:border-primary-400 dark:sm:text-primary-300'
                    : 'text-neutral-500 sm:border-transparent hover:text-neutral-800 dark:hover:text-neutral-200 sm:hover:border-neutral-300 dark:sm:hover:border-neutral-600'
                }`}
              >
                <span className="inline-flex items-center gap-1 sm:gap-2">
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="sm:hidden">{tab.shortLabel}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </span>
                <span
                  className={`text-[10px] sm:text-[11px] tabular-nums px-1 sm:px-1.5 py-0.5 rounded-md leading-none ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                      : 'bg-neutral-200/80 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400 sm:bg-neutral-100'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search + Format-Filter (templates only) */}
      {activeTab !== 'variables' && (
        <div className="fade-in flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder="Suchen…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 !py-2 sm:!py-2.5 text-[16px] sm:text-sm"
            />
          </div>
          <div
            className="grid grid-cols-3 sm:inline-flex w-full sm:w-auto items-stretch sm:items-center gap-0.5 p-0.5 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/80"
            role="group"
            aria-label="Format filtern"
          >
            {formatFilterOptions.map((opt) => {
              const Icon = opt.icon;
              const active = formatFilter === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFormatFilter(opt.id)}
                  className={`inline-flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-medium transition-colors touch-manipulation ${
                    active
                      ? 'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50 shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
                  }`}
                  aria-pressed={active}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Gespeicherte Entwürfe (localStorage) */}
      {activeTab !== 'variables' && templateDrafts.length > 0 && (
        <div className="fade-in rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-1.5">
                <Save className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                Entwürfe
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                Lokal gespeichert, bis du sie löschst oder als Vorlage erstellst
              </p>
            </div>
            <span className="text-[11px] tabular-nums px-1.5 py-0.5 rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
              {templateDrafts.length}
            </span>
          </div>
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {templateDrafts.map((draft) => {
              const title = String(draft.formData?.name || '').trim() || 'Unbenannter Entwurf';
              const subject = String(draft.formData?.subject || '').trim();
              const typeLabel =
                templateTypes.find((t) => t.value === draft.formData?.type)?.label || draft.formData?.type;
              const updated = draft.updatedAt
                ? new Date(draft.updatedAt).toLocaleString('de-DE', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : null;
              return (
                <li
                  key={draft.id}
                  className="flex items-start gap-3 px-4 sm:px-5 py-3.5 hover:bg-neutral-50/80 dark:hover:bg-neutral-800/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                      {title}
                    </p>
                    {subject ? (
                      <p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300 truncate">
                        {subject}
                      </p>
                    ) : null}
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      {typeLabel}
                      {draft.editorMode === 'design' ? ' · Design' : ''}
                      {updated ? ` · ${updated}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => openDraft(draft)}
                      className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                      title="Weiterbearbeiten"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => requestDeleteDraft(draft)}
                      className="p-2 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                      title="Entwurf löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Tab: Templates */}
      {activeTab === 'templates' && (
        <div className="fade-in rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-800">
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">E-Mail-Vorlagen</h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Einfache Vorlagen mit eigenem Namen — Text oder Design
            </p>
          </div>
          {filteredTemplates.length === 0 ? (
            <EmptyState
              title={hasListFilter ? 'Keine Treffer' : 'Noch keine Vorlagen'}
              description={
                hasListFilter
                  ? listFilterHint
                  : 'Lege eine neue E-Mail-Vorlage an und vergib einen Namen.'
              }
              actionLabel={!hasListFilter ? 'Vorlage erstellen' : undefined}
              onAction={!hasListFilter ? () => openNewTemplate('custom') : undefined}
            />
          ) : (
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {filteredTemplates
                .slice()
                .sort((a, b) =>
                  String(a.name || '').localeCompare(String(b.name || ''), 'de', {
                    sensitivity: 'base',
                  })
                )
                .map((t) => renderTemplateRow(t))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Variables */}
      {activeTab === 'variables' && (
        <div className="fade-in rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-800 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Platzhalter</h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                Variablen gelten nur für die gewählte Vorlage
                {varsTemplateId
                  ? ` · ${templateVars.length} Variablen${contactCount > 0 ? ` · ${contactCount} aus Kontakt` : ''}${customCount > 0 ? ` · ${customCount} fest` : ''}`
                  : ''}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto lg:items-center">
              <SelectField
                value={varsTemplateId}
                onChange={(e) => {
                  setVarsTemplateId(e.target.value);
                  resetVarForm();
                }}
                className="input-field text-sm min-w-[14rem]"
              >
                <option value="">Vorlage wählen…</option>
                {templates.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </SelectField>
              <div className="relative w-full sm:w-56 flex-shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4 pointer-events-none" />
                <input
                  type="text"
                  value={varSearchTerm}
                  onChange={(e) => setVarSearchTerm(e.target.value)}
                  placeholder="Variablen suchen…"
                  className="input-field pl-10 text-sm"
                  disabled={!varsTemplateId}
                />
              </div>
            </div>
          </div>

          {!varsTemplateId ? (
            <EmptyState
              title="Vorlage wählen"
              description="Jede E-Mail-Vorlage hat eigene Platzhalter. Wähle oben eine Vorlage, um ihre Variablen zu bearbeiten."
            />
          ) : null}

          {varsTemplateId && showVarForm && (
            <form
              onSubmit={handleSaveVariable}
              className="form-slide-in px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/70 dark:bg-neutral-950/40 space-y-3"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                  {editingVar ? 'Variable bearbeiten' : 'Neue Variable'}
                </p>
                <button
                  type="button"
                  onClick={resetVarForm}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Variable
                  </label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-mono text-neutral-400 select-none">{'{{'}</span>
                    <input
                      type="text"
                      value={varForm.key}
                      onChange={(e) =>
                        setVarForm({
                          ...varForm,
                          key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(),
                        })
                      }
                      className="input-field font-mono text-sm flex-1"
                      placeholder="ansprechpartner"
                      required
                      disabled={usesSystemSource || (editingVar && isProtectedDefaultVar(editingVar))}
                    />
                    <span className="text-sm font-mono text-neutral-400 select-none">{'}}'}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Quelle
                  </label>
                  {usesSystemSource ? (
                    <input
                      type="text"
                      className="input-field text-sm bg-neutral-50 dark:bg-neutral-950/50"
                      value={sourceLabel(varForm.source)}
                      readOnly
                      disabled
                    />
                  ) : (
                    <SelectField
                      value={varForm.source}
                      onChange={(e) => {
                        const source = e.target.value;
                        const field = String(source || '').startsWith('contact:')
                          ? source.slice('contact:'.length)
                          : '';
                        const suggested = field ? `contact_${field}` : '';
                        const shortOrEmpty =
                          !varForm.key ||
                          CONTACT_FIELD_OPTIONS.some(
                            (o) =>
                              o.source.startsWith('contact:') &&
                              (varForm.key === o.source.slice('contact:'.length) ||
                                varForm.key === `contact_${o.source.slice('contact:'.length)}`)
                          );
                        setVarForm({
                          ...varForm,
                          source,
                          ...(shortOrEmpty && suggested ? { key: suggested } : {}),
                        });
                      }}
                      className="input-field text-sm"
                    >
                      {CONTACT_FIELD_OPTIONS.map((opt) => (
                        <option key={opt.source} value={opt.source}>
                          {opt.label}
                        </option>
                      ))}
                    </SelectField>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Beschreibung
                  </label>
                  <input
                    type="text"
                    value={varForm.label}
                    onChange={(e) => setVarForm({ ...varForm, label: e.target.value })}
                    className="input-field text-sm"
                    placeholder="Kurzbeschreibung"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    {usesSystemSource
                      ? 'Abmelde-Link'
                      : usesContactSource
                        ? 'Fallback (wenn Kontakt leer)'
                        : 'Text in der E-Mail'}
                  </label>
                  {usesSystemSource ? (
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950/40 px-3 py-2">
                      Persönlicher Abmelde-Link auf dem Server (
                      <code className="text-xs">/api/u/…</code>
                      ). Wird beim Versand mit Token erzeugt — kein Mailto an Ihr Postfach.
                    </p>
                  ) : (
                    <input
                      type="text"
                      value={varForm.value}
                      onChange={(e) => setVarForm({ ...varForm, value: e.target.value })}
                      className="input-field text-sm"
                      placeholder={usesContactSource ? 'optional' : 'Ersetzungstext'}
                      required={!usesContactSource}
                    />
                  )}
                </div>
              </div>
              {!usesSystemSource && <VariableOptionsFields form={varForm} setForm={setVarForm} />}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={resetVarForm} className="btn-secondary text-sm py-2 px-3">
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={savingVar}
                  className="btn-primary text-sm py-2 px-3 disabled:opacity-60"
                >
                  {editingVar ? 'Speichern' : 'Hinzufügen'}
                </button>
              </div>
            </form>
          )}

          {varsTemplateId && templateVars.length === 0 ? (
            <EmptyState
              title="Noch keine Platzhalter"
              description="Variablen wie {{contact_name}} oder {{contact_company}} für personalisierte Mails."
              actionLabel="Variable hinzufügen"
              onAction={() => {
                setEditingVar(null);
                setVarForm({ ...EMPTY_VAR_FORM() });
                setShowVarForm(true);
              }}
            />
          ) : varsTemplateId && filteredVars.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Keine Variablen für „{varSearchTerm}“ gefunden.
              </p>
            </div>
          ) : varsTemplateId ? (
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {filteredVars.map((row, index) => (
                <li
                  key={row.id}
                  className="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-neutral-50/80 dark:hover:bg-neutral-950/30 transition-colors"
                  style={{
                    animation: `panel-reveal 0.35s cubic-bezier(0.22, 1, 0.36, 1) ${Math.min(index, 8) * 0.035}s both`,
                  }}
                >
                  <div className="sm:w-52 lg:w-60 flex-shrink-0 min-w-0">
                    <code
                      className="inline-flex max-w-full truncate font-mono text-sm px-2 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800/50"
                      title={`{{${row.key}}}`}
                    >
                      {`{{${row.key}}}`}
                    </code>
                  </div>
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate">
                      {row.label || 'Ohne Beschreibung'}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                      {isContactVar(row)
                        ? `${sourceLabel(row.source)}${row.value ? ` · Fallback: ${row.value}` : ''}`
                        : isSystemVar(row)
                          ? sourceLabel(row.source)
                          : row.value || '—'}
                    </p>
                    <div className="text-[11px] text-neutral-400 dark:text-neutral-500 [&_a]:text-primary-600 dark:[&_a]:text-primary-400 [&_a]:underline">
                      <span className="text-neutral-400 dark:text-neutral-500">Vorschau: </span>
                      <span
                        className="text-neutral-600 dark:text-neutral-300"
                        dangerouslySetInnerHTML={{
                          __html: textToHtmlLines(
                            resolvePreviewValue(row, SAMPLE_CONTACT, mailboxEmail, templateVars) ||
                              '—',
                            '#2563eb'
                          ),
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 self-end sm:self-center flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleEditVariable(row)}
                      className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                      title="Bearbeiten"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {!isProtectedDefaultVar(row) && (
                      <button
                        type="button"
                        onClick={() => handleDeleteVariable(row)}
                        className="p-2 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && createPortal(
        <div
          className={`fixed inset-0 z-[100] flex items-center justify-center ${
            editorMode === 'design' ? 'p-0 sm:p-3 lg:p-5' : 'p-2 sm:p-3'
          }`}
        >
          <div className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={resetForm} />
          <div
            className={`relative bg-white dark:bg-neutral-900 text-left overflow-hidden shadow-xl w-full flex flex-col border border-neutral-200 dark:border-neutral-800 scale-in ${
              editorMode === 'design'
                ? 'max-w-[min(100vw,96rem)] h-[100dvh] sm:h-[96vh] rounded-none sm:rounded-2xl border-0 sm:border'
                : 'max-w-6xl h-[96vh] max-h-[96vh] rounded-2xl'
            }`}
          >
              <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
                <div className="flex-shrink-0 px-3 sm:px-5 lg:px-6 pt-2.5 sm:pt-3.5 pb-2.5 sm:pb-3 border-b border-neutral-100 dark:border-neutral-800">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-white truncate">
                      {editingTemplate
                        ? 'Vorlage bearbeiten'
                        : activeDraftId
                          ? 'Neue Vorlage (Entwurf)'
                          : 'Neue Vorlage'}
                    </h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="btn-secondary !py-2 !px-3 text-sm"
                        disabled={savingTemplate}
                      >
                        Abbrechen
                      </button>
                      <button
                        type="submit"
                        className="btn-primary !py-2 !px-3 text-sm"
                        disabled={savingTemplate}
                      >
                        {savingTemplate
                          ? 'Speichern…'
                          : editingTemplate
                            ? 'Aktualisieren'
                            : 'Erstellen'}
                      </button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                        aria-label="Schließen"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden flex flex-col lg:flex-row">
                  <div
                    className="flex flex-col px-3 sm:px-4 lg:px-5 py-2.5 sm:py-3 lg:border-r border-neutral-100 dark:border-neutral-800 flex-shrink-0 lg:flex-1 lg:min-h-0 lg:overflow-hidden"
                    onDragEnter={(e) => {
                      if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
                      e.preventDefault();
                      e.stopPropagation();
                      attachmentDragDepthRef.current += 1;
                      setAttachmentDragOver(true);
                    }}
                    onDragOver={(e) => {
                      if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
                    }}
                    onDragLeave={(e) => {
                      if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
                      e.preventDefault();
                      e.stopPropagation();
                      attachmentDragDepthRef.current = Math.max(0, attachmentDragDepthRef.current - 1);
                      if (attachmentDragDepthRef.current === 0) setAttachmentDragOver(false);
                    }}
                    onDrop={(e) => {
                      if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
                      e.preventDefault();
                      e.stopPropagation();
                      attachmentDragDepthRef.current = 0;
                      setAttachmentDragOver(false);
                      addPendingAttachments(e.dataTransfer?.files);
                    }}
                  >
                    <div
                      className={
                        editorMode === 'design'
                          ? 'flex flex-col gap-2 sm:gap-3 lg:flex-1 lg:min-h-0'
                          : 'flex flex-col gap-2.5 lg:flex-1 lg:min-h-0'
                      }
                    >
                      <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        <div className="min-w-0">
                          <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            Name *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="input-field !py-2 text-sm"
                            required
                          />
                        </div>
                        <div className="min-w-0">
                          <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            Betreff *
                          </label>
                          <input
                            ref={subjectRef}
                            type="text"
                            value={formData.subject}
                            onFocus={() => setInsertTarget('subject')}
                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                            className="input-field !py-2 text-sm"
                            placeholder="Betreff der E-Mail"
                            required
                          />
                        </div>
                      </div>

                      <div className="min-h-[12rem] sm:min-h-[16rem] flex flex-col lg:flex-1 lg:min-h-0">
                        <div className="flex-shrink-0 flex items-center justify-between gap-2 mb-2">
                          <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300 sm:hidden">
                            Inhalt *
                          </span>
                          <div className="inline-flex rounded-lg border border-neutral-200 dark:border-neutral-700 p-0.5 bg-neutral-50 dark:bg-neutral-950/40 sm:ml-auto">
                            <button
                              type="button"
                              onClick={() => switchEditorMode('text')}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                editorMode === 'text'
                                  ? 'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 shadow-sm'
                                  : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
                              }`}
                            >
                              <AlignLeft className="w-3.5 h-3.5" />
                              Text
                            </button>
                            <button
                              type="button"
                              onClick={() => switchEditorMode('design')}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                editorMode === 'design'
                                  ? 'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 shadow-sm'
                                  : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
                              }`}
                            >
                              <LayoutTemplate className="w-3.5 h-3.5" />
                              Design
                            </button>
                          </div>
                        </div>

                        {editorMode === 'design' ? (
                          <EmailDesignBuilder
                            className="flex-1 min-h-0"
                            value={design || createEmptyDesign()}
                            onChange={setDesign}
                            variables={templateVars}
                            placeholderMap={designPlaceholderMap}
                          />
                        ) : (
                          <div
                            className="quill-container email-message-input reading-composer-quill flex-1 min-h-0 flex flex-col"
                            onFocusCapture={() => setInsertTarget('content')}
                          >
                            <ReactQuill
                              key={contentEditorKey}
                              ref={(instance) => {
                                quillRef.current = instance;
                                if (instance) {
                                  window.requestAnimationFrame(() => {
                                    try {
                                      prepareEmailComposerQuill(instance.getEditor?.());
                                    } catch (_) {
                                      /* ignore */
                                    }
                                  });
                                }
                              }}
                              theme="snow"
                              defaultValue={formData.content || '<p><br></p>'}
                              onChange={handleContentQuillChange}
                              modules={quillModules}
                              formats={quillFormats}
                              placeholder="Hallo {{contact_name}}, ich bin auf Ihr Unternehmen {{contact_company}} aufmerksam geworden..."
                              className="flex-1 min-h-0 flex flex-col"
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex-shrink-0 relative">
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                            Anhänge
                            {(existingAttachments.length + pendingAttachments.length) > 0 ? (
                              <span className="ml-1 text-[11px] font-normal text-neutral-400 tabular-nums">
                                ({existingAttachments.length + pendingAttachments.length})
                              </span>
                            ) : null}
                          </label>
                          <button
                            type="button"
                            className="btn-secondary text-[11px] !py-1 !px-2 inline-flex items-center"
                            onClick={() => attachmentInputRef.current?.click()}
                          >
                            <Paperclip className="w-3 h-3 mr-1" />
                            Datei
                          </button>
                          <MediaAttachmentPickerButton
                            onSelect={(files) => addPendingAttachments(files)}
                            maxFiles={Math.max(0, 10 - existingAttachments.length - pendingAttachments.length)}
                          />
                          <span className="text-[10px] text-neutral-400">
                            max. 10 · je 10 MB
                          </span>
                          <input
                            ref={attachmentInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              addPendingAttachments(e.target.files);
                              e.target.value = '';
                            }}
                          />
                        </div>

                        {(existingAttachments.length > 0 || pendingAttachments.length > 0) && (
                          <ul className="mt-1.5 flex flex-wrap gap-1.5">
                            {existingAttachments.map((att) => (
                              <li
                                key={`ex-${att.id}`}
                                className="inline-flex items-center gap-1.5 max-w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2 py-1"
                              >
                                <Paperclip className="w-3 h-3 text-neutral-400 flex-shrink-0" />
                                <button
                                  type="button"
                                  onClick={() => openTemplateAttachmentPreview(att)}
                                  className="text-[11px] text-neutral-700 dark:text-neutral-200 truncate hover:underline max-w-[10rem] text-left"
                                  title={att.filename}
                                >
                                  {att.filename}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeExistingAttachment(att.id)}
                                  className="p-0.5 rounded text-neutral-400 hover:text-danger-600"
                                  title="Entfernen"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </li>
                            ))}
                            {pendingAttachments.map((file, index) => (
                              <li
                                key={`new-${file.name}-${index}`}
                                className="inline-flex items-center gap-1.5 max-w-full rounded-md border border-primary-200 dark:border-primary-800 bg-primary-50/40 dark:bg-primary-950/20 px-2 py-1"
                              >
                                <Paperclip className="w-3 h-3 text-primary-500 flex-shrink-0" />
                                <span className="text-[11px] text-neutral-700 dark:text-neutral-200 truncate max-w-[10rem]" title={file.name}>
                                  {file.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removePendingAttachment(index)}
                                  className="p-0.5 rounded text-neutral-400 hover:text-danger-600"
                                  title="Entfernen"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}

                        {attachmentDragOver ? (
                          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border border-dashed border-primary-500 bg-primary-50/90 dark:bg-primary-950/80 pointer-events-none">
                            <p className="text-xs font-medium text-primary-700 dark:text-primary-200">
                              Dateien ablegen
                            </p>
                          </div>
                        ) : null}
                      </div>

                      {editorMode === 'text' ? (
                        <div className="flex-shrink-0 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50/80 dark:bg-neutral-950/40 p-3 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <label className="flex items-center gap-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-300">
                                <Braces className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                                Prompt für externe KI
                              </label>
                              <p className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed">
                                Plain Text zum Kopieren — enthält alle Platzhalter-Infos für ChatGPT,
                                Claude &amp; Co.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={handleCopyExternalAiPrompt}
                              className="btn-secondary text-[11px] !py-1.5 !px-2.5 inline-flex items-center flex-shrink-0"
                              title="Prompt in die Zwischenablage kopieren"
                            >
                              <Copy className="w-3.5 h-3.5 mr-1.5" />
                              Kopieren
                            </button>
                          </div>

                          {templateVars.length === 0 ? (
                            <p className="text-[11px] text-amber-700 dark:text-amber-300">
                              Noch keine Platzhalter angelegt — unten (bzw. rechts) Variablen für
                              diese Vorlage anlegen, dann aktualisiert sich dieser Prompt automatisch.
                            </p>
                          ) : null}

                          <textarea
                            readOnly
                            value={externalAiPrompt}
                            rows={10}
                            className="input-field resize-y text-[11px] font-mono leading-relaxed min-h-[9rem] bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200"
                            onFocus={(e) => e.target.select()}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <aside
                    className={`w-full flex-shrink-0 flex flex-col border-t lg:border-t-0 border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950/50 lg:min-h-0 lg:self-stretch lg:overflow-hidden ${
                      editorMode === 'design'
                        ? 'lg:w-[14rem] xl:w-[15rem]'
                        : 'lg:w-[22rem] xl:w-[24rem]'
                    }`}
                  >
                    <div
                      className={`lg:flex-1 lg:min-h-0 lg:overflow-y-auto ${
                        editorMode === 'design' ? 'px-2 py-2 space-y-2' : 'px-3 py-3 space-y-3'
                      }`}
                    >
                      <div className="lg:hidden flex items-center gap-2 pb-1">
                        <Braces className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                        <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                          Variablen
                          {templateVars.length > 0 ? (
                            <span className="ml-1.5 font-normal text-neutral-500 dark:text-neutral-400">
                              ({templateVars.length})
                            </span>
                          ) : null}
                        </p>
                      </div>
                      {showModalVarForm ? (
                        <div
                          className={`rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 ${
                            editorMode === 'design' ? 'p-2 space-y-1.5' : 'p-3 space-y-2.5'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p
                              className={`font-semibold text-neutral-800 dark:text-neutral-100 ${
                                editorMode === 'design' ? 'text-xs' : 'text-sm'
                              }`}
                            >
                              Neue Variable
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setShowModalVarForm(false);
                                setModalVarForm({ ...EMPTY_VAR_FORM() });
                              }}
                              className="p-1 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                              Variable
                            </label>
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-mono text-neutral-400 select-none">{'{{'}</span>
                              <input
                                type="text"
                                value={modalVarForm.key}
                                onChange={(e) =>
                                  setModalVarForm({
                                    ...modalVarForm,
                                    key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase(),
                                  })
                                }
                                className="input-field font-mono text-sm !py-1.5 flex-1"
                                placeholder="contact_anrede"
                              />
                              <span className="text-xs font-mono text-neutral-400 select-none">{'}}'}</span>
                            </div>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                              Quelle
                            </label>
                            <SelectField
                              value={modalVarForm.source}
                              onChange={(e) => {
                                const source = e.target.value;
                                const field = String(source || '').startsWith('contact:')
                                  ? source.slice('contact:'.length)
                                  : '';
                                const suggested = field ? `contact_${field}` : '';
                                const shortOrEmpty =
                                  !modalVarForm.key ||
                                  CONTACT_FIELD_OPTIONS.some(
                                    (o) =>
                                      o.source.startsWith('contact:') &&
                                      (modalVarForm.key === o.source.slice('contact:'.length) ||
                                        modalVarForm.key ===
                                          `contact_${o.source.slice('contact:'.length)}`)
                                  );
                                setModalVarForm({
                                  ...modalVarForm,
                                  source,
                                  ...(shortOrEmpty && suggested ? { key: suggested } : {}),
                                });
                              }}
                              className="input-field text-sm !py-1.5"
                            >
                              {CONTACT_FIELD_OPTIONS.map((opt) => (
                                <option key={opt.source} value={opt.source}>
                                  {opt.label}
                                </option>
                              ))}
                            </SelectField>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                              Beschreibung
                            </label>
                            <input
                              type="text"
                              value={modalVarForm.label}
                              onChange={(e) => setModalVarForm({ ...modalVarForm, label: e.target.value })}
                              className="input-field text-sm !py-1.5"
                              placeholder="z. B. Anrede (Sehr geehrte…)"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                              {String(modalVarForm.source || '').startsWith('contact:')
                                ? 'Fallback (wenn Kontakt leer)'
                                : 'Text in der E-Mail'}
                            </label>
                            <input
                              type="text"
                              value={modalVarForm.value}
                              onChange={(e) => setModalVarForm({ ...modalVarForm, value: e.target.value })}
                              className="input-field text-sm !py-1.5"
                              placeholder={
                                String(modalVarForm.source || '').startsWith('contact:')
                                  ? 'optional'
                                  : 'Ersetzungstext'
                              }
                            />
                          </div>
                          <VariableOptionsFields form={modalVarForm} setForm={setModalVarForm} compact />
                          <button
                            type="button"
                            onClick={handleSaveModalVariable}
                            className="btn-primary w-full justify-center text-sm !py-2 disabled:opacity-60"
                          >
                            {savingModalVar ? 'Speichern…' : 'Erstellen & einfügen'}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setModalVarForm({ ...EMPTY_VAR_FORM() });
                            setShowModalVarForm(true);
                          }}
                          className={`w-full inline-flex items-center justify-center gap-1 rounded-lg border border-dashed border-neutral-300 dark:border-neutral-600 font-medium text-neutral-600 dark:text-neutral-300 hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300 ${
                            editorMode === 'design'
                              ? 'px-2 py-1.5 text-[10px]'
                              : 'gap-1.5 px-3 py-2 text-xs'
                          }`}
                        >
                          <Plus className={editorMode === 'design' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
                          Variable erstellen
                        </button>
                      )}

                      {templateVars.length === 0 && !showModalVarForm ? (
                        <p
                          className={`text-neutral-500 dark:text-neutral-400 text-center leading-relaxed ${
                            editorMode === 'design' ? 'text-[10px] px-1 py-2' : 'text-sm px-1 py-4'
                          }`}
                        >
                          Noch keine Variablen für diese Vorlage. Erstelle z. B. eine Anrede aus dem Kontakt.
                        </p>
                      ) : editorMode === 'design' ? (
                        <div className="space-y-2">
                          {templateVars.length > 0 ? (
                            <p className="text-[9px] text-neutral-500 dark:text-neutral-400 px-0.5 leading-snug">
                              Klick fügt in aktives Feld ein
                            </p>
                          ) : null}
                          {[
                            {
                              id: 'contact',
                              title: 'Kontakt',
                              items: templateVars.filter(isContactVar),
                            },
                            {
                              id: 'custom',
                              title: 'Fest',
                              items: templateVars.filter((v) => !isContactVar(v)),
                            },
                          ]
                            .filter((group) => group.items.length > 0)
                            .map((group) => (
                              <div key={group.id} className="space-y-1">
                                <p className="text-[9px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 px-0.5">
                                  {group.title}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {group.items.map((v) => (
                                    <button
                                      key={v.id || v.key}
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => insertPlaceholder(v.key)}
                                      className="px-1.5 py-0.5 rounded text-[9px] font-mono leading-tight border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-primary-700 dark:text-primary-300 hover:border-primary-400 hover:bg-primary-50/50 dark:hover:bg-primary-950/30 transition-colors"
                                      title={`${sourceLabel(v.source)}${v.label ? ` · ${v.label}` : ''}${!isContactVar(v) && v.value ? ` · ${v.value}` : ''}`}
                                    >
                                      {`{{${v.key}}}`}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <>
                          {templateVars.length > 0 ? (
                            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 px-0.5">
                              Klick fügt ein in{' '}
                              <strong className="text-neutral-700 dark:text-neutral-200">
                                {insertTarget === 'subject' ? 'Betreff' : 'Nachricht'}
                              </strong>
                              {' · '}
                              Feld vorher anklicken
                            </p>
                          ) : null}
                          {[
                          {
                            id: 'contact',
                            title: 'Aus Kontakt',
                            hint: 'Wert vom gewählten Kontakt',
                            items: templateVars.filter(isContactVar),
                          },
                          {
                            id: 'custom',
                            title: 'Fester Text',
                            hint: 'Immer derselbe Wert',
                            items: templateVars.filter((v) => !isContactVar(v)),
                          },
                        ]
                          .filter((group) => group.items.length > 0)
                          .map((group) => (
                            <div key={group.id} className="space-y-1.5">
                              <div>
                                <p className="text-xs font-semibold text-neutral-800 dark:text-neutral-100">
                                  {group.title}
                                </p>
                                <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                                  {group.hint}
                                </p>
                              </div>
                              <div className="space-y-1">
                                {group.items.map((v) => (
                                  <button
                                    key={v.id || v.key}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => insertPlaceholder(v.key)}
                                    className="w-full text-left rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2.5 py-1.5 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/40 dark:hover:bg-primary-950/30 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    title={`${sourceLabel(v.source)}${v.label ? ` · ${v.label}` : ''}${!isContactVar(v) && v.value ? ` · ${v.value}` : ''}`}
                                  >
                                    <span className="block text-sm font-mono font-semibold text-primary-700 dark:text-primary-300 leading-snug">
                                      {`{{${v.key}}}`}
                                    </span>
                                    <span className="block text-xs text-neutral-600 dark:text-neutral-300 mt-0.5 leading-snug">
                                      {v.label || (isContactVar(v)
                                        ? sourceLabel(v.source).replace(/^Kontakt → /, '')
                                        : 'Fester Text')}
                                    </span>
                                    {!isContactVar(v) && v.value ? (
                                      <span className="block text-[11px] text-neutral-400 dark:text-neutral-500 mt-0.5 truncate">
                                        {String(v.value)}
                                      </span>
                                    ) : null}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </aside>
                </div>

              </form>
          </div>
        </div>,
        document.body
      )}

      {/* Preview Modal */}
      {showPreview && previewTemplate && createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-5">
          <div
            className="absolute inset-0 bg-neutral-950/70 backdrop-blur-sm"
            onClick={() => setShowPreview(false)}
          />

          <div
            className={`relative w-full max-h-[94vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl border scale-in ${
              isDesignPreview
                ? 'max-w-3xl border-neutral-700 bg-neutral-900'
                : 'max-w-2xl border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900'
            }`}
          >
              <div
                className={`flex-shrink-0 flex items-center justify-between gap-3 px-5 py-4 border-b ${
                  isDesignPreview
                    ? 'border-neutral-800'
                    : 'border-neutral-100 dark:border-neutral-800'
                }`}
              >
                <div className="min-w-0">
                  <h3
                    className={`text-base font-semibold truncate ${
                      isDesignPreview ? 'text-white' : 'text-neutral-900 dark:text-neutral-100'
                    }`}
                  >
                    Vorschau
                  </h3>
                  <p
                    className={`text-xs truncate mt-0.5 ${
                      isDesignPreview ? 'text-neutral-400' : 'text-neutral-500 dark:text-neutral-400'
                    }`}
                  >
                    {previewTemplate.name}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className={`p-1.5 rounded-lg flex-shrink-0 ${
                    isDesignPreview
                      ? 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                      : 'text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div
                className={`flex-shrink-0 px-5 py-4 border-b space-y-3 ${
                  isDesignPreview
                    ? 'border-neutral-800'
                    : 'border-neutral-100 dark:border-neutral-800'
                }`}
              >
                <div
                  className={`grid grid-cols-[4.5rem_1fr] gap-x-3 gap-y-1.5 text-sm ${
                    isDesignPreview ? 'text-neutral-300' : ''
                  }`}
                >
                  <span className="text-neutral-400">Von</span>
                  <span className={`truncate ${isDesignPreview ? 'text-neutral-200' : 'text-neutral-800 dark:text-neutral-200'}`}>
                    Webentwicklungs-Team
                  </span>
                  <span className="text-neutral-400">An</span>
                  <span className={`truncate ${isDesignPreview ? 'text-neutral-200' : 'text-neutral-800 dark:text-neutral-200'}`}>
                    {SAMPLE_CONTACT.name} &lt;{SAMPLE_CONTACT.email}&gt;
                  </span>
                  <span className="text-neutral-400">Betreff</span>
                  <span
                    className={`font-semibold leading-snug ${
                      isDesignPreview ? 'text-white' : 'text-neutral-900 dark:text-neutral-50'
                    }`}
                  >
                    {String(previewTemplate.subject || '').trim()
                      ? renderHighlightedText(
                          previewTemplate.subject,
                          SAMPLE_CONTACT,
                          templateVars,
                          mailboxEmail
                        )
                      : isReplyTemplateType(previewTemplate.type)
                        ? 'Betreff der vorherigen Mail behalten'
                        : '(Kein Betreff)'}
                  </span>
                </div>
                {!isDesignPreview && (
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-neutral-500">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-primary-400" />
                      Kontakt-Variable
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-accent-400" />
                      Feste Variable
                    </span>
                  </div>
                )}
                {isDesignPreview && (
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-neutral-500">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-sky-400" />
                      Kontakt-Variable
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-amber-400" />
                      Feste Variable
                    </span>
                  </div>
                )}
              </div>

              <div
                className={`flex-1 min-h-0 overflow-y-auto ${
                  isDesignPreview ? 'px-3 py-3 sm:px-4 sm:py-4 bg-neutral-950' : 'px-5 py-6 sm:px-7 sm:py-7'
                }`}
              >
                {renderPreviewBody(previewTemplate)}
                {(previewTemplate.attachments || []).length > 0 && (
                  <div
                    className={`mt-6 pt-4 border-t ${
                      isDesignPreview
                        ? 'border-neutral-800 px-2'
                        : 'border-neutral-100 dark:border-neutral-800'
                    }`}
                  >
                    <p className="text-xs font-medium text-neutral-500 mb-2 flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5" />
                      Anhänge ({previewTemplate.attachments.length})
                    </p>
                    <ul className="space-y-1.5">
                      {previewTemplate.attachments.map((att) => (
                        <li key={att.id} className="flex items-baseline gap-2 min-w-0">
                          <button
                            type="button"
                            onClick={() => openTemplateAttachmentPreview(att)}
                            className="text-sm text-primary-700 dark:text-primary-300 hover:underline text-left truncate"
                          >
                            {att.filename}
                          </button>
                          <span className="text-neutral-400 text-xs flex-shrink-0">
                            {formatFileSize(att.size)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div
                className={`flex-shrink-0 px-5 py-3 border-t flex items-center justify-between gap-3 ${
                  isDesignPreview
                    ? 'border-neutral-800 bg-neutral-900'
                    : 'border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950/40'
                }`}
              >
                <p className="text-xs text-neutral-500 truncate flex items-center gap-1.5 min-w-0">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  Beispieldaten von {SAMPLE_CONTACT.company}
                </p>
                <button type="button" onClick={() => setShowPreview(false)} className="btn-primary flex-shrink-0">
                  Schließen
                </button>
              </div>
          </div>
        </div>,
        document.body
      )}

      {variableToDelete && createPortal(
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => !deletingVariable && setVariableToDelete(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Variable löschen?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    Möchtest du die Variable{' '}
                    <code className="font-mono text-sm px-1.5 py-0.5 rounded-md bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100">
                      {`{{${variableToDelete.key}}}`}
                    </code>
                    {variableToDelete.label ? (
                      <>
                        {' '}
                        <span className="text-neutral-500">({variableToDelete.label})</span>
                      </>
                    ) : null}{' '}
                    wirklich löschen? Platzhalter in dieser Vorlage bleiben als Text stehen.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                disabled={deletingVariable}
                onClick={confirmDeleteVariable}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
              >
                {deletingVariable ? 'Löschen…' : 'Löschen'}
              </button>
              <button
                type="button"
                disabled={deletingVariable}
                onClick={() => setVariableToDelete(null)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {templateToDelete && createPortal(
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => !deletingTemplate && setTemplateToDelete(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Vorlage löschen?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    Möchtest du die Vorlage{' '}
                    <span className="font-medium text-neutral-900 dark:text-neutral-100">
                      „{templateToDelete.name}“
                    </span>{' '}
                    unwiderruflich löschen?
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                disabled={deletingTemplate}
                onClick={confirmDeleteTemplate}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
              >
                {deletingTemplate ? 'Löschen…' : 'Löschen'}
              </button>
              <button
                type="button"
                disabled={deletingTemplate}
                onClick={() => setTemplateToDelete(null)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showTextModeConfirm && createPortal(
        <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => setShowTextModeConfirm(false)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center flex-shrink-0">
                  <AlignLeft className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Zum Textmodus wechseln?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    Das komplette Design wird verworfen. Im Textmodus bleibt nur der lesbare Text aus den Blöcken.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                onClick={confirmSwitchToTextMode}
                className="inline-flex items-center justify-center rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-3.5 py-2"
              >
                Zum Text wechseln
              </button>
              <button
                type="button"
                onClick={() => setShowTextModeConfirm(false)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {leaveConfirmOpen && createPortal(
        <div className="fixed inset-0 z-[240] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => setLeaveConfirmOpen(false)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 flex items-center justify-center flex-shrink-0">
                  <Save className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    {editingTemplate ? 'Änderungen verwerfen?' : 'Vorlage verlassen?'}
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    {editingTemplate
                      ? 'Es gibt ungespeicherte Änderungen. Möchtest du sie verwerfen oder weiter bearbeiten?'
                      : 'Du hast Änderungen an der neuen Vorlage. Als Entwurf speichern oder verwerfen?'}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-4 sm:px-5 pt-3 pb-4 sm:py-3 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-950/40">
              {editingTemplate ? (
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:justify-end sm:gap-1.5">
                  <button
                    type="button"
                    onClick={() => setLeaveConfirmOpen(false)}
                    className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 rounded-xl sm:rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm sm:text-xs font-medium px-3 py-2.5 sm:px-2.5 sm:py-1.5 hover:bg-white dark:hover:bg-neutral-800"
                  >
                    Weiter bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={discardAndClose}
                    className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 rounded-xl sm:rounded-md bg-danger-600 hover:bg-danger-700 text-white text-sm sm:text-xs font-medium px-3 py-2.5 sm:px-2.5 sm:py-1.5"
                  >
                    Verwerfen
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row-reverse sm:justify-start sm:gap-1.5">
                  <button
                    type="button"
                    onClick={saveDraftAndClose}
                    className="inline-flex w-full sm:w-auto items-center justify-center min-h-[44px] sm:min-h-0 rounded-xl sm:rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm sm:text-xs font-semibold sm:font-medium px-3 py-2.5 sm:px-2.5 sm:py-1.5"
                  >
                    Als Entwurf speichern
                  </button>
                  <div className="grid grid-cols-2 gap-2 sm:contents">
                    <button
                      type="button"
                      onClick={discardAndClose}
                      className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 rounded-xl sm:rounded-md border border-danger-200 dark:border-danger-800 text-danger-700 dark:text-danger-300 text-sm sm:text-xs font-medium px-3 py-2.5 sm:px-2.5 sm:py-1.5 hover:bg-danger-50 dark:hover:bg-danger-900/50"
                    >
                      Verwerfen
                    </button>
                    <button
                      type="button"
                      onClick={() => setLeaveConfirmOpen(false)}
                      className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 rounded-xl sm:rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm sm:text-xs font-medium px-3 py-2.5 sm:px-2.5 sm:py-1.5 hover:bg-white dark:hover:bg-neutral-800"
                    >
                      Weiter bearbeiten
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

      {draftToDelete && createPortal(
        <div className="fixed inset-0 z-[240] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => setDraftToDelete(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Entwurf löschen?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    „{String(draftToDelete.formData?.name || '').trim() || 'Unbenannter Entwurf'}“
                    wird unwiderruflich entfernt.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-1.5 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                onClick={() => setDraftToDelete(null)}
                className="inline-flex items-center justify-center rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-xs font-medium px-2.5 py-1.5 hover:bg-white dark:hover:bg-neutral-800"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={confirmDeleteDraft}
                className="inline-flex items-center justify-center rounded-md bg-danger-600 hover:bg-danger-700 text-white text-xs font-medium px-2.5 py-1.5"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Templates;
